// mixpanel.js
'use strict';
import { v4 as uuidv4 } from 'uuid';
import isEmpty from 'lodash/isEmpty';

import { mixpanel as mixpanelDebug } from '../resource/debug.js';
import env from '../resource/env.js';
import {
  getGenericOSName,
  getDeviceType,
  getUserAgent,
  getIsOnWebviewApp,
} from '../resource/getUserAgent.js';
import i18n from '../resource/i18n.js';
import {
  getMixpanelViewId,
  getMixpanelViewIdAndPathParameter,
} from '../resource/getMixpanelViewId.js';
import { DrmRobustnessPriority } from '../resource/drmConstants.js';
import loadScriptTag from '../resource/loadScriptTag.js';
import { EventTypes } from '../resource/trackingConstants.js';
import { getSentry, Severity } from '../resource/sentry.js';
import { Accuracy } from '../selector/getTimestampOffset.js';
import {
  PlayerType,
  EngagementLivestreamState,
} from '../resource/engagementConstants.js';

const mixpanelLog = mixpanelDebug.extend('log:mixpanel');
let pubSubController;
let replayController;

// Dedup set to prevent sending the same event to PubSub on Mixpanel SDK retries.
// The SDK retries the same batch every 5s on any failed flush (network error,
// ad blocker, server error, etc.), re-triggering before_send_events each time.
// Page reload also causes retries: the SDK restores undelivered events from
// localStorage and re-flushes them, but this Set is reset on each page load so
// cross-session retries are not covered.
// $insert_id is stable across retries (generated once per track() call by the SDK).
const pubSubSentInsertIds = new Set();
const MAX_DEDUP_SIZE = 10000;

const MIXPANEL_SCRIPT = `(function(f,b){if(!b.__SV){var e,g,i,h;window.mixpanel=b;b._i=[];b.init=function(e,f,c){function g(a,d){var b=d.split(".");2==b.length&&(a=a[b[0]],d=b[1]);a[d]=function(){a.push([d].concat(Array.prototype.slice.call(arguments,0)))}}var a=b;"undefined"!==typeof c?a=b[c]=[]:c="mixpanel";a.people=a.people||[];a.toString=function(a){var d="mixpanel";"mixpanel"!==c&&(d+="."+c);a||(d+=" (stub)");return d};a.people.toString=function(){return a.toString(1)+".people (stub)"};i="disable time_event track track_pageview track_links track_forms track_with_groups add_group set_group remove_group register register_once alias unregister identify name_tag set_config reset opt_in_tracking opt_out_tracking has_opted_in_tracking has_opted_out_tracking clear_opt_in_out_tracking start_batch_senders people.set people.set_once people.unset people.increment people.append people.union people.track_charge people.clear_charges people.delete_user people.remove".split(" ");
for(h=0;h<i.length;h++)g(a,i[h]);var j="set set_once union unset remove delete".split(" ");a.get_group=function(){function b(c){d[c]=function(){call2_args=arguments;call2=[c].concat(Array.prototype.slice.call(call2_args,0));a.push([e,call2])}}for(var d={},e=["get_group"].concat(Array.prototype.slice.call(arguments,0)),c=0;c<j.length;c++)b(j[c]);return d};b._i.push([e,f,c])};b.__SV=1.2;e=f.createElement("script");e.type="text/javascript";e.async=!0;e.src="undefined"!==typeof MIXPANEL_CUSTOM_LIB_URL?MIXPANEL_CUSTOM_LIB_URL:"file:"===f.location.protocol&&"//cdn.mxpnl.com/libs/mixpanel-2-latest.min.js".match(/^\\/\\//)?"https://cdn.mxpnl.com/libs/mixpanel-2-latest.min.js":"//cdn.mxpnl.com/libs/mixpanel-2-latest.min.js";g=f.getElementsByTagName("script")[0];g.parentNode.insertBefore(e,g)}})(document,window.mixpanel||[]);`;

/** Will Mixpanel events go to dev envrionment */
export const isDevEnvironment =
  typeof window !== 'undefined' &&
  /localhost|.app\.swag\.live$/.test(window.location.hostname);

export const processMixpanelApiCallCore = ({ apiNames, args }) => {
  const log = mixpanelLog.extend('processMixpanelApiCall');
  log(
    'init',
    'apiNames:',
    apiNames,
    ', args:',
    args,
    ', queue:',
    mixpanelApiCallQueue
  );
  if (!window.mixpanel) {
    throw new Error('missing mixpanel instance');
  }

  let tempApi = window.mixpanel;
  let _this = window.mixpanel;
  apiNames.forEach(name => {
    _this = tempApi;
    tempApi = tempApi?.[name];
  });

  const result = tempApi?.apply?.(_this, args);
  log('done');
  return result;
};

let mixpanelApiCallQueue = [];

export const processMixpanelApiCall = ({ apiNames, args }) => {
  const log = mixpanelLog.extend('processMixpanelApiCall');
  if (!window.mixpanel) {
    log('enqueue', 'apiNames:', apiNames, ', args:', args);
    return mixpanelApiCallQueue.push({ apiNames, args });
  }
  return processMixpanelApiCallCore({ apiNames, args });
};

export const getViewId = getMixpanelViewId;

export const getDataFromPath = getMixpanelViewIdAndPathParameter;

const AVAILABILITY_TRACK_KEY = '_shouldTrackMixpanelUnavailable';

/** @type {boolean | null} */
let shouldTrackUnavailable = null;

export const getShouldTrackUnavailable = async () => {
  if (shouldTrackUnavailable === null) {
    const { getItem } = await import('../resource/persist.js');
    shouldTrackUnavailable = await getItem(AVAILABILITY_TRACK_KEY);
  }
  return shouldTrackUnavailable;
};

/**
 * Begin to track mixpanel availability, would update result of getShouldTrackUnavailable if fails too many times in sequence
 */
export const trackAvailability = () => {
  if (typeof window === 'undefined') return;
  const _ping = (url, timeout) => {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.timeout = timeout;
      xhr.onload = () => resolve(true);
      xhr.ontimeout = () => reject(new Error('request timeout'));
      xhr.onerror = () => reject(new Error(xhr.status));
      xhr.open('POST', url, true);
      xhr.send();
    });
  };

  let failedTimesInSequence = 0;
  let pingTimeoutHandle = null;

  const ping = async () => {
    let isAvailable = false;
    try {
      const url = new URL('track/', 'https://api-js.mixpanel.com');
      url.searchParams.append('ip', '1');
      url.searchParams.append('_', new Date().getTime().toString());
      isAvailable = await _ping(url.href, 5000);
    } catch (err) {
      // no-op
    } finally {
      const { setItem, removeItem } = await import('../resource/persist.js');
      if (isAvailable) {
        // reset failed times and ping again later
        setTimeout(ping, 30000); // TODO: remote config
        failedTimesInSequence = 0;
        // Unmark
        if (await getShouldTrackUnavailable()) {
          shouldTrackUnavailable = false;
          await removeItem(AVAILABILITY_TRACK_KEY);
        }
      } else {
        failedTimesInSequence++;
        if (failedTimesInSequence < 3) {
          // Retry later
          pingTimeoutHandle = setTimeout(ping, 2000); // TODO: remote config
        } else {
          // Mark as unavailable
          clearTimeout(pingTimeoutHandle);
          shouldTrackUnavailable = true;
          await setItem(AVAILABILITY_TRACK_KEY, true);
        }
      }
    }
  };
  pingTimeoutHandle = setTimeout(ping, 5000); // TODO: remote config
};

/**
 * Set Name on user's Mixpanel profile
 * @param {Object} arg
 * @param {Object} arg.name
 */
export const setName = async ({ name }) => {
  return processMixpanelApiCall({
    apiNames: ['people', 'set'],
    args: [{ $name: name }],
  });
};

/**
 * Set Email on user's Mixpanel profile
 * @param {Object} arg
 * @param {Object} arg.email
 */
export const setEmail = async ({ email }) => {
  return processMixpanelApiCall({
    apiNames: ['people', 'set'],
    args: [{ $email: email }],
  });
};

/**
 * Set utm on Mixpanel and remove unused properties
 * @param {Object} arg
 * @param {Object} arg.utm
 */
export const updateMixpanelUtm = async ({ utm }) => {
  setPrependData({ utm });

  // Unregister unused utm properties
  const utmKeys = [
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_content',
    'utm_term',
  ];
  utmKeys.forEach(key => {
    // Need to unregister UTM super property
    // to prevent mixpanel MERGE origin/new utm value from URL
    if (!utm[key]) {
      processMixpanelApiCall({ apiNames: ['unregister'], args: [key] });
    }
  });
};

/**
 * The metadata of tracking event
 * @typedef {Object} EventMeta
 * @property {string} browser
 * @property {string} flavor
 * @property {string} language
 * @property {string} os
 * @property {string} platform
 * @property {string} version
 * @property {string} app
 */

/**
 * The UTM data
 * @typedef {Object} UtmData
 * @property {string} utm_source
 * @property {string} utm_medium
 * @property {string} utm_campaign
 */

/**
 * The payload of tracking event
 * @typedef {Object} EventPayload
 * @property {EventMeta} metadata
 * @property {string} viewId
 * @property {string} modalId
 * @property {string} messageId
 * @property {any} abData
 * @property {Object} [extraProps]
 */

/**
 * The event object for tracking
 * @typedef {Object} TrackedEvent
 * @property {string} type - the type of event, also as event name for mixpanel
 * @property {EventPayload} payload - the data of the event
 */

const refineVersionValue = () => {
  const version = env.TAG_NAME || env.BRANCH_NAME || 'local';
  if (version.indexOf('v') === 0) {
    return version.slice(1);
  }

  return version;
};

/**
 * Get language metadata.
 * @returns {object} return object with 'metadata.language' property and the value is converted i18n.language.
 */
const getLanguageMetadata = () => {
  if (i18n?.language) {
    let language = i18n.language.toLowerCase();

    if (language === 'zh-cn') {
      language = 'zh';
    } else if (language === 'zh-tw') {
      language = 'zh-hant';
    } else {
      language = language?.replace(/-[A-Z]+/i, '');
    }

    return {
      'metadata.language': language,
    };
  }

  return {};
};

const defaultMeta = {
  'metadata.browser': 'web',
  ...getLanguageMetadata(),
  'metadata.os': getGenericOSName(),
  'metadata.platform': getDeviceType() || 'desktop',
  'metadata.version': refineVersionValue(),
  'metadata.user_agent': getUserAgent()?.ua,
};

let remoteMeta = {};

/**
 * Set the meta to the local variable
 * @param {Object} arg
 * @param {Object} arg.data - metadata from pusher remote config
 */
export const setRemoteMeta = ({ data }) => {
  remoteMeta = Object.assign({}, remoteMeta, data, getLanguageMetadata());

  // to prevent using session id from shared pusher in service worker
  if (typeof window !== 'undefined' && window.__sessionId__) {
    remoteMeta['metadata.session.id'] = window.__sessionId__;
  }
};

let prependData = {
  utm: {},
};

let fpjsData = {};

/**
 * Get current metadata snapshot for external consumers (e.g. Sentry Structured Logging).
 * Returns the merged result of defaultMeta, remoteMeta, and fpjsData.
 * @returns {Object} current metadata key-value pairs
 */
export const getLogMeta = () => refinePayload({});

/**
 * Prepend fpjs data properties to subsequent events
 * @param {object} data
 */
export const setFpJsData = async data => {
  if (data.visitorId) {
    processMixpanelApiCall({
      apiNames: ['people', 'union'],
      args: ['fpId', data.visitorId],
    });
  }
  Object.keys(data).forEach(key => {
    fpjsData[`fpjs.${key}`] = data[key];
  });
};

/**
 * Set the utm
 * @param {Object} arg
 * @param {Object} arg.utm
 */
export const setPrependData = ({ utm = {} }) => {
  let data = {};

  if (typeof utm === 'object' && Object.keys(utm).length > 0) {
    data.utm = utm;
  }

  prependData = Object.assign({}, prependData, data);
};

const AFFILIATE_PREFIX_FIELD = [
  'affise',
  'ao',
  'c_track',
  'c_aff',
  'c_off',
  'voluum',
];

/**
 *
 * @param {EventPayload} payload
 * @return {EventPayload}
 */
const refinePayload = payload => {
  /** @type {UtmData} */
  const utm = prependData.utm
    ? Object.keys(prependData.utm).reduce((acc, key) => {
        if (key.startsWith('utm_')) {
          return { ...acc, [key]: prependData.utm[key] };
        }

        let affiliateKey = undefined;
        AFFILIATE_PREFIX_FIELD.forEach(affiliate => {
          if (key.startsWith(affiliate) && prependData.utm[key]) {
            affiliateKey = `affiliate.${key.replace(
              `${affiliate}_`,
              `${affiliate}.`
            )}`;
          }
        });
        if (affiliateKey)
          return { ...acc, [affiliateKey]: prependData.utm[key] };

        return acc;
      }, {})
    : {};

  /** refine the meta with prefix `metadata.` */
  const meta = payload.metadata
    ? Object.keys(payload.metadata).reduce(
        (acc, key) =>
          payload.metadata[key] != null
            ? { ...acc, [`metadata.${key}`]: payload.metadata[key] }
            : acc,
        {}
      )
    : {};
  delete payload.metadata;

  /** refine the ab testing data from payload */
  const ab = payload.abData
    ? Object.keys(payload.abData).reduce(
        (acc, key) => ({ ...acc, [`ab.${key}`]: payload.abData[key] }),
        {}
      )
    : {};
  delete payload.abData;

  const data = Object.assign(
    {},
    defaultMeta,
    payload,
    meta,
    remoteMeta,
    utm,
    ab,
    fpjsData
  );

  return Object.keys(data).reduce(
    (acc, key) => (data[key] != null ? { ...acc, [key]: data[key] } : acc),
    {}
  );
};

/**
 * Wrap the mixpanel track method
 * @param {TrackedEvent} event - the event object
 */
const track = async ({ type, payload }) => {
  const timestampOffsetMilliseconds = window.__ntp__
    ? await window.__ntp__.asyncGetNtpTimestampOffset(Accuracy.MILLISECOND)
    : 0;
  const timestamp = Date.now() + timestampOffsetMilliseconds;
  const refinedData = refinePayload(payload);

  if (payload?.shouldSendBeacon) {
    return processMixpanelApiCall({
      apiNames: ['track'],
      args: [
        type,
        { time: timestamp, ...refinedData },
        { transport: 'sendBeacon' },
      ],
    });
  } else {
    return processMixpanelApiCall({
      apiNames: ['track'],
      args: [type, { time: timestamp, ...refinedData }],
    });
  }
};

/**
 * Handle the event by the event type
 * @param {TrackedEvent} event - the event object
 */
const eventReducer = async ({ type, payload }) => {
  if (typeof window === 'undefined') return;

  if (!payload?.shouldSendBeacon) {
    await new Promise(resolve => window.requestIdleCallback(resolve));
  }
  switch (type) {
    case EventTypes.PLAYER_VISIBLE:
    case EventTypes.PLAYER_STARTED:
    case EventTypes.PLAYER_STOPPED:
    case EventTypes.PLAYER_HIDDEN:
      return _playerTracked(type, payload);
    case EventTypes.USER_CLICKED:
      return _userClicked(payload);
    case EventTypes.VIEW_OPENED:
      return _viewOpened(payload);
    case EventTypes.VIEW_CLOSED:
      return _viewClosed(payload);
    case EventTypes.HAPPYHOUR_NAVBAR_CLICKED:
      return _happyhourNavbarClicked(payload);
    case EventTypes.HAPPYHOUR_CONTENT_PAGE_GREEN_BANNER_CLICKED:
      return _happyhourContentPageGreenBannerClicked(payload);
    case EventTypes.HAPPYHOUR_CONTENT_PAGE_RED_BANNER_CLICKED:
      return _happyhourContentPageRedBannerClicked(payload);
    case EventTypes.HAPPYHOUR_CONTENT_PAGE_LOGO_CLICKED:
      return _happyhourContentPageLogoClicked(payload);
    case EventTypes.HAPPYHOUR_OVERLAY_INFO_CLICKED:
      return _happyhourOverlayInfoClicked(payload);
    case EventTypes.HAPPYHOUR_OVERLAY_SHAREBUTTON:
      return _happyhourOverlaySharebutton(payload);
    case EventTypes.HAPPYHOUR_PAGE_OPENED:
      return _happyhourPageOpened(payload);
    case EventTypes.CHAT_TEMPLATE_OPENED:
      return _chatTemplateOpened(payload);
    case EventTypes.NOTIFICATION_DISPLAYED:
      return _notificationDisplayed(payload);
    case EventTypes.NOTIFICATION_CLICKED:
      return _notificationClicked(payload);
    case EventTypes.PUSH_NOTIFICATION_RECEIVED:
      return _pushNotificationReceived(payload);
    case EventTypes.PUSH_NOTIFICATION_CLICKED:
      return _pushNotificationClicked(payload);
    case EventTypes.USER_FLIX_NO_SEEK:
      return _userFlixNoSeek(payload);
    case EventTypes.USER_FLIX_REGULAR_SEEK:
      return _userFlixRegularSeek(payload);
    case EventTypes.USER_SESSION_TICK:
      return _userSessionTick(payload);
    case EventTypes.USER_WATERMARK_STYLE_CHANGED:
      return _userWatermarkStyleChanged(payload);
    case EventTypes.USER_WATERMARK_VALIDATE:
      return _userWatermarkValidate(payload);
    case EventTypes.USER_WATERMARK_INTERSECION_VALIDATE:
      return _userWatermarkIntersectionValidate(payload);
    case EventTypes.USER_VIDEO_LOCATION_CHANGED:
      return _userVideoLocationChanged(payload);
    case EventTypes.USER_VIDEO_SET_CONTROLS:
      return _userVideoSetControls(payload);
    case EventTypes.USER_VIDEO_ENTER_PIP:
      return _userVideoEnterPip(payload);
    case EventTypes.USER_NON_PLAYER_ENTER_FULLSCREEN:
      return _userNonPlayerEnterFullscreen(payload);
    case EventTypes.USER_FLIX_DISTURBED:
      return _userFlixDisturbed(payload);
    case EventTypes.REVENUE_KEEPER:
      return _revenueKeeper(payload);
    case EventTypes.USER_FLIX_GOT_HELP:
      return _userFlixGotHelp(payload);
    case EventTypes.USER_TRY_OVERRIDE_WINDOW_FUNCTION:
      return _userTryOverrideWindowFunction(payload);
    case EventTypes.USER_FLIX_TRY_MODIFY_Z_INDEX:
      return _userFlixTryModifyZIndex(payload);
    case EventTypes.USER_FLIX_SEEK_BEHAVIOR:
      return _userFlixSeekBehavior(payload);
    case EventTypes.USER_BACKGROUND_ENHANCED:
      return _userBackgroundEnhanced(payload);
    case EventTypes.LIVESTREAM_PAYMENT:
      return _liveStreamPayment(payload);
    case EventTypes.LIVESTREAM_PAYMENT_CANCELED:
      return _liveStreamPaymentCancel(payload);
    case EventTypes.LIVESTREAM_STREAM_AUTHORIZED:
      return _liveStreamStreamAuthorized(payload);
    case EventTypes.LIVESTREAM_CHANGE_PRESET:
      return _liveStreamChangePreset(payload);
    case EventTypes.LIVESTREAM_TOKEN_GOT:
      return _liveStreamTokenGot(payload);
    case EventTypes.LIVESTREAM_PLAYER_FALLBACK:
      return _liveStreamPlayerFallback(payload);
    case EventTypes.LIVESTREAM_PLAYER_BUFFER_STALLED:
      return _liveStreamPlayerBufferStalled(payload);
    case EventTypes.LIVESTREAM_PLAYER_AUTOPLAY_FAILED:
      return _liveStreamPlayerAutoplayFailed(payload);
    case EventTypes.LIVESTREAM_PLAYER_WAITING:
      return _liveStreamPlayerPlayerWaiting(payload);
    case EventTypes.LIVESTREAM_FETCH_TOKEN_START:
      return _liveStreamFetchTokenStart(payload);
    case EventTypes.LIVESTREAM_FETCH_TOKEN_ERROR:
      return _liveStreamFetchTokenError(payload);
    case EventTypes.PLAYER_ERROR:
      return _playerError(payload);
    case EventTypes.AUDIO_PLAYER_ERROR:
      return _audioPlayerError(payload);
    case EventTypes.PUSHER_EVENT_GOT:
      return _pusherEventGot(payload);
    case EventTypes.USER_DEBUG_SENT:
      return _userDebugSent(payload);
    case EventTypes.USER_LOGGED_IN:
      return _userLoggedIn(payload);
    case EventTypes.PUSHER_CHANNEL_STALLED:
      return _pusherChannelStalled(payload);
    case EventTypes.MESSAGE_CREATE_FAILED:
    case EventTypes.MESSAGE_UPDATE_FAILED:
      return _messageFailed({
        ...payload,
        type,
      });
    case EventTypes.SEARCH_STARTED:
      return _searchStarted(payload);
    case EventTypes.SEARCH_ENDED:
      return _searchEnded(payload);
    case EventTypes.ORDER_CREATION_SUCCESS:
      return _orderCreationSuccess(payload);
    case EventTypes.ORDER_CREATION_FAILED:
      return _orderCreationFailed(payload);
    case EventTypes.USER_WEB_VITALS_METRIC_SENT:
      return _userWebVitalsMetricSent(payload);
    case EventTypes.USER_DEBUG_FEED_CONTENT_ERROR:
      return _userDebugFeedContentError(payload);
    case EventTypes.USER_DEBUG_NETWORKING_REQUEST:
      return _userDebugNetworkingRequest(payload);
    case EventTypes.USER_DEBUG_VERSION_CHANGED:
      return _userDebugVersionChanged(payload);
    case EventTypes.USER_DEBUG_FCM_TOKEN:
      return _userDebugFcmToken(payload);
    case EventTypes.USER_DEBUG_MESSAGE_PRICE:
      return _userDebugMessagePrice(payload);
    case EventTypes.USER_DEBUG_PLAYER_ERROR:
      return _userDebugPlayerError(payload);
    case EventTypes.USER_TOKEN_GOT:
      return _userTokenGot(payload);
    case EventTypes.LIVESTREAM_PAYMENT_ERROR:
      return _liveStreamPaymentError(payload);
    case EventTypes.LIVESTREAM_EXCLUSIVE_STATUS_CHANGE:
      return _liveStreamExclusiveStatusChange(payload);
    case EventTypes.LIVESTREAM_EXCLUSIVE_PREPARING_TIMER:
      return _liveStreamExclusivePreparingTimer(payload);
    case EventTypes.LIVESTREAM_VIEWER_CAMERA_PREVIEW_FAILED:
      return _livestreamViewerCameraPreviewFailed(payload);
    case EventTypes.LIVESTREAM_VIEWER_CAMERA_PUBLISH_ATTEMPT:
      return _livestreamViewerCameraPublishAttempt(payload);
    case EventTypes.LIVESTREAM_VIEWER_CAMERA_PUBLISH_SUCCEEDED:
      return _livestreamViewerCameraPublishSucceeded(payload);
    case EventTypes.LIVESTREAM_VIEWER_CAMERA_PUBLISH_FAILED:
      return _livestreamViewerCameraPublishFailed(payload);
    case EventTypes.LIVESTREAM_VIEWER_CAMERA_PUBLISH_STOPPED:
      return _livestreamViewerCameraPublishStopped(payload);
    case EventTypes.FEED_FILTER_APPLIED:
      return _feedFilterApplied(payload);
    case EventTypes.MEDIA_ENGAGEMENT:
      return _mediaEngagement(payload);
    case EventTypes.PLAYER_FREEZE_LOG:
      return _playerFreezeLog(payload);
    case EventTypes.LIVESTREAM_GIFT_ANIMATION_PLAYED:
      return _livestreamGiftAnimationPlayed(payload);
    case EventTypes.SHOP_GOOGLE_PAY_CLICKED:
      return _shopGooglePayClicked();
  }
};

export default eventReducer;

/**
 * Handle the EventTypes.PLAYER_FREEZE_LOG event.
 * @kind mixpanel/eventType
 * @name _playerFreezeLog
 * @param {Object} payload
 * @param {string} payload.streamId - stream id.
 * @param {string} payload.sessionId - session id.
 * @param {string} payload.provider - stream provider.
 * @param {string} payload.remoteUserId - Agora remote user ID.
 * @param {number} payload.averageFreezeRate - average freeze rate for the remote user.
 * @return {void}
 */
const _playerFreezeLog = payload => {
  const { streamId, sessionId, provider, remoteUserId, averageFreezeRate } =
    payload;
  const finalizedPayload = {
    'stream.id': streamId,
    'stream.sessionId': sessionId,
    'stream.provider': provider,
    'agora.remoteUserId': remoteUserId,
    'stream.averageFreezeRate': averageFreezeRate,
  };
  track({ type: EventTypes.PLAYER_FREEZE_LOG, payload: finalizedPayload });
};

/**
 * Get the time offset seconds between server and client
 * @param {number} serverTimestamp - server timestamp
 * @param {number} clientTimestamp - client timestamp
 * @returns {number} - time offset seconds
 */
const getTimeOffsetSeconds = (serverTimestamp, clientTimestamp) => {
  if (serverTimestamp == null || clientTimestamp == null) {
    return;
  }
  return Math.floor((serverTimestamp - clientTimestamp) / 1000);
};

/**
 * Handle the following events:
 * PLAYER_VISIBLE, PLAYER_STARTED, PLAYER_STOPPED, PLAYER_HIDDEN
 * @param {string} type
 * @param {EventPayload} payload
 * @return {void}
 */
const _playerTracked = (
  type,
  {
    metadata,
    viewId,
    modalId,
    messageId,
    streamId,
    abData,
    shouldSendBeacon,
    extraProps = {},
  } = {}
) => {
  const data = {
    metadata,
    'modal.id': modalId,
    'view.id': viewId,
    'message.id': messageId,
    'stream.id': streamId,
    abData,
    shouldSendBeacon,
    ...extraProps,
  };

  track({ type, payload: data });
};

/**
 * Handle the EventTypes.USER_CLICKED event
 * @param {EventPayload} payload
 * @return {void}
 */
const _userClicked = ({
  metadata,
  viewId,
  buttonId,
  messageId,
  modalId,
  abData,
  extraProps = {},
} = {}) => {
  const data = {
    metadata,
    'modal.id': modalId,
    'view.id': viewId,
    'button.id': buttonId,
    'message.id': messageId,
    abData,
    ...extraProps,
  };

  track({ type: EventTypes.USER_CLICKED, payload: data });
};

/**
 * Handle the EventTypes.VIEW_OPENED event
 * @param {EventPayload} payload
 * @return {void}
 */
const _viewOpened = ({
  metadata,
  viewId,
  modalId,
  messageId,
  abData,
  extraProps = {},
} = {}) => {
  const data = {
    metadata,
    'view.id': viewId,
    'modal.id': modalId,
    'message.id': messageId,
    abData,
    ...extraProps,
  };

  track({ type: EventTypes.VIEW_OPENED, payload: data });
};

/**
 * Handle the EventTypes.VIEW_CLOSED event
 * @param {EventPayload} payload
 * @return {void}
 */
const _viewClosed = ({
  metadata,
  viewId,
  modalId,
  messageId,
  abData,
  extraProps = {},
} = {}) => {
  const data = {
    metadata,
    'view.id': viewId,
    'modal.id': modalId,
    'message.id': messageId,
    abData,
    ...extraProps,
  };

  track({ type: EventTypes.VIEW_CLOSED, payload: data });
};

const _searchStarted = ({
  metadata,
  sessionId,
  viewId,
  extraProps = {},
} = {}) => {
  const data = {
    metadata,
    'view.id': viewId,
    'search.session.id': sessionId,
    ...extraProps,
  };

  track({ type: EventTypes.SEARCH_STARTED, payload: data });
};

const _searchEnded = ({
  metadata,
  sessionId,
  viewId,
  keyword,
  autocompleteList,
  duration,
  extraProps = {},
} = {}) => {
  const data = {
    metadata,
    'view.id': viewId,
    'search.session.id': sessionId,
    'search.keyword': keyword,
    'search.autocomple.list': autocompleteList,
    'search.duration': duration,
    ...extraProps,
  };

  track({ type: EventTypes.SEARCH_ENDED, payload: data });
};

/**
 * Handle HAPPYHOUR_NAVBAR_CLICKED event.
 * @kind mixpanel/eventType
 * @name _happyhourNavbarClicked
 * @param {string} {userId} - userId
 * @param {string} {platform} - desktop | iOS | Android
 */
const _happyhourNavbarClicked = ({ userId, platform }) => {
  track({
    type: EventTypes.HAPPYHOUR_NAVBAR_CLICKED,
    payload: { userId, platform },
  });
};

/**
 * Handle HAPPYHOUR_CONTENT_PAGE_GREEN_BANNER_CLICKED event.
 * @kind mixpanel/eventType
 * @name _happyhourContentPageGreenBannerClicked
 * @param {string} {userId} - userId
 * @param {string} {platform} - desktop | iOS | Android
 */
const _happyhourContentPageGreenBannerClicked = ({ userId, platform }) => {
  track({
    type: EventTypes.HAPPYHOUR_CONTENT_PAGE_GREEN_BANNER_CLICKED,
    payload: {
      userId,
      platform,
    },
  });
};

/**
 * Handle HAPPYHOUR_CONTENT_PAGE_RED_BANNER_CLICKED event.
 * @kind mixpanel/eventType
 * @name _happyhourContentPageRedBannerClicked
 * @param {string} {userId} - userId
 * @param {string} {platform} - desktop | iOS | Android
 */
const _happyhourContentPageRedBannerClicked = ({ userId, platform }) => {
  track({
    type: EventTypes.HAPPYHOUR_CONTENT_PAGE_RED_BANNER_CLICKED,
    payload: {
      userId,
      platform,
    },
  });
};

/**
 * Handle HAPPYHOUR_CONTENT_PAGE_LOGO_CLICKED event.
 * @kind mixpanel/eventType
 * @name _happyhourContentPageLogoClicked
 * @param {string} {userId} - userId
 * @param {string} {platform} - desktop | iOS | Android
 */
const _happyhourContentPageLogoClicked = ({ userId, platform }) => {
  track({
    type: EventTypes.HAPPYHOUR_CONTENT_PAGE_LOGO_CLICKED,
    payload: {
      userId,
      platform,
    },
  });
};

/**
 * Handle HAPPYHOUR_OVERLAY_INFO_CLICKED event.
 * @kind mixpanel/eventType
 * @name _happyhourOverlayInfoClicked
 * @param {string} {userId} - userId
 * @param {string} {platform} - desktop | iOS | Android
 */
const _happyhourOverlayInfoClicked = ({ userId, platform }) => {
  track({
    type: EventTypes.HAPPYHOUR_OVERLAY_INFO_CLICKED,
    payload: { userId, platform },
  });
};

/**
 * Handle HAPPYHOUR_OVERLAY_SHAREBUTTON event.
 * @kind mixpanel/eventType
 * @name _happyhourOverlaySharebutton
 * @param {string} {userId} - userId
 * @param {string} {platform} - desktop | iOS | Android
 * @param {number} {state} - 0: after event start, 1: event inactive, -1: before event start
 */
const _happyhourOverlaySharebutton = ({ userId, platform, state }) => {
  track({
    type: EventTypes.HAPPYHOUR_OVERLAY_SHAREBUTTON,
    payload: {
      userId,
      platform,
      state,
    },
  });
};

/**
 * Handle HAPPYHOUR_PAGE_OPENED event.
 * @kind mixpanel/eventType
 * @name _happyhourPageOpened
 * @param {string} {userId} - userId
 * @param {string} {platform} - desktop | iOS | Android
 */
const _happyhourPageOpened = ({ userId, platform }) => {
  track({
    type: EventTypes.HAPPYHOUR_PAGE_OPENED,
    payload: {
      userId,
      platform,
    },
  });
};

/**
 * Handle CHAT_TEMPLATE_OPENED event.
 * @kind mixpanel/eventType
 * @name _chatTemplateOpened
 * @param {string} {userUsername} - user username.
 */
const _chatTemplateOpened = ({ userUsername }) => {
  track({
    type: EventTypes.CHAT_TEMPLATE_OPENED,
    payload: {
      'user.username': userUsername,
    },
  });
};

/**
 * Handle NOTIFICATION_DISPLAYED event.
 * @kind mixpanel/eventType
 * @name _notificationDisplayed
 * @param {String} {id} - notification id.
 * @param {String} {type} - notification embedding data type.
 * @param {String} {position} - display position.
 * @param {String} {link} - notification link.
 * @param {number} {exp} - notification exp.
 * @param {String} {viewId} - current path viewId.
 */
const _notificationDisplayed = ({ id, type, position, link, exp, viewId }) => {
  track({
    type: EventTypes.NOTIFICATION_DISPLAYED,
    payload: {
      'notification.id': id,
      'notification.type': type,
      'notification.position': position,
      'notification.link': link,
      'notification.exp': exp,
      'view.id': viewId,
    },
  });
};

/**
 * Handle NOTIFICATION_CLICKED event.
 * @kind mixpanel/eventType
 * @name _notificationClicked
 * @param {String} {id} - notification id.
 * @param {String} {type} - notification embedding data type.
 * @param {String} {position} - display position.
 * @param {String} {link} - notification link.
 * @param {String} {viewId} - current path viewId.
 * @param {String} {buttonId} - button id.
 */
const _notificationClicked = ({
  id,
  type,
  position,
  link,
  viewId,
  buttonId,
}) => {
  track({
    type: EventTypes.NOTIFICATION_CLICKED,
    payload: {
      'notification.id': id,
      'notification.type': type,
      'notification.position': position,
      'notification.link': link,
      'view.id': viewId,
      'button.id': buttonId,
    },
  });
};

/**
 * Handle PUSH_NOTIFICATION_RECEIVED event.
 * @kind mixpanel/eventType
 * @name _pushNotificationReceived
 * @param {string} title
 * @param {string} body
 * @param {string} type
 * @param {string} id
 * @param {string} url
 * @param {string} username
 * @param {number} timestamp
 * @param {string} fcmWebpushPayload - raw FCM webpush payload
 * @param {string} fcmMessageId - FCM message identifier
 * @param {string} requestTranslationLanguage - requested translation language
 * @param {string} errorMessage - notification display error message
 */
const _pushNotificationReceived = ({
  title,
  body,
  type,
  id,
  url,
  username,
  fcmWebpushPayload,
  timestamp,
  requestTranslationLanguage,
  errorMessage,
  fcmMessageId,
}) => {
  track({
    type: EventTypes.PUSH_NOTIFICATION_RECEIVED,
    payload: {
      'push.notification.title': title,
      'push.notification.body': body,
      'push.notification.type': type,
      'push.notification.id': id,
      'push.notification.link.url': url,
      'push.notification.received_timestamp': timestamp,
      'user.username': username,
      'push.notification.fcm_message_id': fcmMessageId,
      'push.notification.fcm_webpush_payload': fcmWebpushPayload,
      'push.notification.request_translation_language':
        requestTranslationLanguage,
      'push.notification.error_message': errorMessage,
    },
  });
};

/**
 * Handle PUSH_NOTIFICATION_CLICKED event.
 * @kind mixpanel/eventType
 * @name _notificationClicked
 * @param {string} title
 * @param {string} body
 * @param {string} type
 * @param {string} id
 * @param {string} url
 * @param {string} username
 */
const _pushNotificationClicked = ({
  title,
  body,
  type,
  id,
  url,
  username,
  timestamp,
}) => {
  track({
    type: EventTypes.PUSH_NOTIFICATION_CLICKED,
    payload: {
      'push.notification.title': title,
      'push.notification.body': body,
      'push.notification.type': type,
      'push.notification.id': id,
      'push.notification.link.url': url,
      'push.notification.clicked_timestamp': timestamp,
      'user.username': username,
    },
  });
};

/**
 * Handle USER_FLIX_NO_SEEK event.
 * @kind mixpanel/eventType
 * @name _userFlixNoSeek
 * @param {String} {messageId} - message id.
 * @param {String} {userUsername} - cp username.
 * @param {number} {noSeekDuration} - flix no seek duration.
 * @param {number} {messageDuration} - message duration.
 */
const _userFlixNoSeek = ({
  messageId,
  userUsername,
  noSeekDuration,
  messageDuration,
}) => {
  track({
    type: EventTypes.USER_FLIX_NO_SEEK,
    payload: {
      'message.id': messageId,
      'user.username': userUsername,
      'no-seek.duration': noSeekDuration,
      'message.duration': messageDuration,
    },
  });
};

/**
 * Handle USER_FLIX_REGULAR_SEEK event.
 * @kind mixpanel/eventType
 * @name _userFlixRegularSeek
 * @param {String} {messageId} - message id.
 * @param {String} {userUsername} - cp username.
 * @param {number} {regularSeekDuration} - flix regular seek duration.
 * @param {number} {messageDuration} - message duration.
 */
const _userFlixRegularSeek = ({
  messageId,
  userUsername,
  regularSeekDuration,
  messageDuration,
}) => {
  track({
    type: EventTypes.USER_FLIX_REGULAR_SEEK,
    payload: {
      'message.id': messageId,
      'user.username': userUsername,
      'regular.duration': regularSeekDuration,
      'message.duration': messageDuration,
    },
  });
};

/**
 * Handle USER_SESSION_TICK event.
 * @kind mixpanel/eventType
 * @name _userSessionTick
 * @param {String} {viewId} - view id.
 * @param {Number} {sessionDuration} - session duration.
 * @param {Number} {windowInnerWidth} - window inner width.
 * @param {Number} {windowInnerHeight} - window inner height.
 */
const _userSessionTick = ({
  viewId,
  sessionDuration,
  windowInnerWidth,
  windowInnerHeight,
}) => {
  track({
    type: EventTypes.USER_SESSION_TICK,
    payload: {
      'view.id': viewId,
      'session.duration': sessionDuration,
      'window.innerHeight': windowInnerHeight,
      'window.innerWidth': windowInnerWidth,
    },
  });
};

/**
 * Handle USER_WATERMARK_STYLE_CHANGED event.
 * @kind mixpanel/eventType
 * @name _userWatermarkStyleChanged
 * @param {String} {messageId} - message id.
 * @param {String} {userUsername} - cp username.
 * @param {String} {viewId} - view id.
 * @param {array} {changedStyleList} - changed style
 */
const _userWatermarkStyleChanged = ({
  messageId,
  userUsername,
  changedStyleList,
  viewId,
}) => {
  track({
    type: EventTypes.USER_WATERMARK_STYLE_CHANGED,
    payload: {
      'view.id': viewId,
      'message.id': messageId,
      'user.username': userUsername,
      'change.styles': changedStyleList,
    },
  });
};

/**
 * Handle USER_WATERMARK_VALIDATE event.
 * @kind mixpanel/eventType
 * @name _userWatermarkValidate
 * @param {String} {messageId} - message id.
 * @param {String} {userUsername} - cp username.
 * @param {String} {viewId} - view id.
 * @param {number} {filledPixelCount}
 * @param {String} {computedStyleFont}
 * @param {String} {error}
 */
const _userWatermarkValidate = ({
  messageId,
  userUsername,
  viewId,
  filledPixelCount,
  computedStyleFont,
  error,
}) => {
  track({
    type: EventTypes.USER_WATERMARK_VALIDATE,
    payload: {
      'view.id': viewId,
      'message.id': messageId,
      'user.username': userUsername,
      'watermark.computedStyle.font': computedStyleFont,
      'watermark.validation.filledPixelCount': filledPixelCount,
      'watermark.validation.error': error,
    },
  });
};

/**
 * Handle USER_WATERMARK_INTERSECION_VALIDATE event.
 * @kind mixpanel/eventType
 * @name _userWatermarkIntersectionValidate
 * @param {String} {messageId} - message id.
 * @param {String} {userUsername} - cp username.
 * @param {String} {viewId} - view id.
 * @param {object} {report}
 */
const _userWatermarkIntersectionValidate = ({
  messageId,
  userUsername,
  viewId,
  report,
}) => {
  track({
    type: EventTypes.USER_WATERMARK_INTERSECION_VALIDATE,
    payload: {
      'view.id': viewId,
      'message.id': messageId,
      'user.username': userUsername,
      ...Object.keys(report).reduce(
        (acc, key) => ({
          ...acc,
          [`watermark.validation.${key}`]: report[key],
        }),
        {}
      ),
    },
  });
};

/**
 * Handle USER_VIDEO_LOCATION_CHANGED event.
 * @kind mixpanel/eventType
 * @name _userVideoLocationChanged
 * @param {String} {messageId} - message id.
 * @param {String} {userUsername} - cp username.
 * @param {String} {viewId} - view id.
 */
const _userVideoLocationChanged = ({ messageId, userUsername, viewId }) => {
  track({
    type: EventTypes.USER_VIDEO_LOCATION_CHANGED,
    payload: {
      'message.id': messageId,
      'user.username': userUsername,
      'view.id': viewId,
    },
  });
};

/**
 * Handle USER_VIDEO_SET_CONTROLS event.
 * @kind mixpanel/eventType
 * @name _userVideoSetControls
 * @param {String} {messageId} - message id.
 * @param {String} {userUsername} - cp username.
 * @param {String} {viewId} - view id.
 * @param {String} {controlsValue} - value of controls attribute
 */
const _userVideoSetControls = ({
  messageId,
  userUsername,
  viewId,
  controlsValue,
}) => {
  track({
    type: EventTypes.USER_VIDEO_SET_CONTROLS,
    payload: {
      'message.id': messageId,
      'user.username': userUsername,
      'view.id': viewId,
      controlsValue,
    },
  });
};

/**
 * Handle USER_VIDEO_ENTER_PIP event.
 * @kind mixpanel/eventType
 * @name _userVideoEnterPip
 */
const _userVideoEnterPip = () => {
  track({
    type: EventTypes.USER_VIDEO_ENTER_PIP,
    payload: {},
  });
};

/**
 * Handle USER_NON_PLAYER_ENTER_FULLSCREEN event.
 * @kind mixpanel/eventType
 * @name _userEnterUnrecognizedFullscreen
 */
const _userNonPlayerEnterFullscreen = ({ nodeHTML }) => {
  track({
    type: EventTypes.USER_NON_PLAYER_ENTER_FULLSCREEN,
    payload: {
      nodeHTML,
    },
  });
};

/**
 * Handle USER_FLIX_DISTURBED event.
 * @kind mixpanel/eventType
 * @name _userFlixDisturbed
 * @param {String} {messageId} - message id.
 * @param {String} {userUsername} - cp username.
 * @param {number} {messageDuration} - message duration.
 * @param {string} {action} - disturbing action.
 * @param {number} {currentTime} - video current time.
 * @param {number} {shuffledTime} - video shuffled time.
 * @param {number} {shuffledPlaybackRate} - flix shuffled playback rate.
 */
const _userFlixDisturbed = ({
  messageId,
  userUsername,
  messageDuration,
  action,
  currentTime,
  shuffledTime,
  shuffledPlaybackRate,
}) => {
  track({
    type: EventTypes.USER_FLIX_DISTURBED,
    payload: {
      'message.id': messageId,
      'user.username': userUsername,
      'message.duration': messageDuration,
      'disturb.action': action,
      'disturb.currentTime': currentTime,
      'disturb.shuffledTime': shuffledTime,
      'disturb.shuffledPlaybackRate': shuffledPlaybackRate,
    },
  });
};

/**
 * Handle REVENUE_KEEPER event.
 * @kind mixpanel/eventType
 * @name _revenueKeeper
 */
const _revenueKeeper = () => {
  track({
    type: EventTypes.REVENUE_KEEPER,
    payload: {},
  });
};

/**
 * Handle USER_FLIX_GOT_HELP event.
 * @kind mixpanel/eventType
 * @name _userFlixGotHelp
 * @param {'set'|'execute'} action
 */
const _userFlixGotHelp = ({ action }) => {
  track({
    type: EventTypes.USER_FLIX_GOT_HELP,
    payload: {
      action,
    },
  });
};

/**
 * Handle USER_TRY_OVERRIDE_WINDOW_FUNCTION event.
 * @kind mixpanel/eventType
 * @name _userTryOverrideWindowFunction
 * @param {string} {functionName} - name of function to be overridden
 * @param {*} {functionValue} - value of function to override to
 */
const _userTryOverrideWindowFunction = ({ functionName, functionValue }) => {
  track({
    type: EventTypes.USER_TRY_OVERRIDE_WINDOW_FUNCTION,
    payload: {
      functionName,
      functionValue: functionValue.toString(),
    },
  });
};

/**
 * Handle USER_FLIX_TRY_MODIFY_Z_INDEX event.
 * @kind mixpanel/eventType
 * @name _userFlixTryModifyZIndex
 * @param {[string]} {zIndexes} - z-indexes from video to root
 * @param {[string]} {positions} - positions from video to root
 * @param {boolean} {isVideoElementMovedOut} - is video element moved to outside of player's element
 */
const _userFlixTryModifyZIndex = ({
  zIndexes,
  positions,
  isVideoElementMovedOut,
}) => {
  track({
    type: EventTypes.USER_FLIX_TRY_MODIFY_Z_INDEX,
    payload: {
      zIndexes,
      positions,
      isVideoElementMovedOut,
    },
  });
};

/**
 * Handle USER_FLIX_SEEK_BEHAVIOR event.
 * @kind mixpanel/eventType
 * @name _userFlixSeekBehavior
 * @param {[string]} {viewId} - view id.
 * @param {[number]} {startTime} - start seek time.
 * @param {[number]} {endTime} - end seek time.
 * @param {[string]} {messageId} - flix message id.
 * @param {[string]} {messageStatus} - unlock status.
 */
const _userFlixSeekBehavior = ({
  viewId,
  startTime,
  endTime,
  messageStatus,
  messageId,
}) => {
  track({
    type: EventTypes.USER_FLIX_SEEK_BEHAVIOR,
    payload: {
      viewId,
      messageId,
      messageStatus,
      'seek.startTime': startTime,
      'seek.endTime': endTime,
      'seek.duration': endTime - startTime,
      'seek.duration.abs': Math.abs(endTime - startTime),
    },
  });
};

/**
 * Handle USER_BACKGROUND_ENHANCED event.
 * @kind mixpanel/eventType
 * @name _userBackgroundEnhanced
 */
const _userBackgroundEnhanced = () => {
  track({
    type: EventTypes.USER_BACKGROUND_ENHANCED,
    payload: {},
  });
};

/**
 * Handle LIVESTREAM_PAYMENT event.
 * @kind mixpanel/eventType
 * @name _liveStreamPayment
 * @param {object} {payload} - tracking payload.
 */
const _liveStreamPayment = payload => {
  track({
    type: EventTypes.LIVESTREAM_PAYMENT,
    payload,
  });
};

/**
 * Handle LIVESTREAM_PAYMENT_CANCELED event.
 * @kind mixpanel/eventType
 * @name _liveStreamPaymentCancel
 * @param {object} {payload} - tracking payload.
 */
const _liveStreamPaymentCancel = payload => {
  track({
    type: EventTypes.LIVESTREAM_PAYMENT_CANCELED,
    payload,
  });
};

/**
 * Handle LIVESTREAM_STREAM_AUTHORIZED event.
 * @kind mixpanel/eventType
 * @name _liveStreamTokenGot
 * @param {string} {streamId} - livestream id.
 * @param {string} {status} - livestream status.
 * @param {string} {preset} - livestream player preset.
 * @param {string} {privateShowStatus} - livestream private show status.
 * @param {number} {tokenNbf} - livestream token nbf.
 * @param {number} {tokenExp} - livestream token exp.
 * @param {boolean} {hasStreamToken} - livestream has stream token.
 * @param {number} {currentTokenExp} - livestream current token exp.
 */
const _liveStreamStreamAuthorized = ({
  streamId,
  status,
  preset,
  privateShowStatus,
  tokenNbf,
  tokenExp,
  hasStreamToken,
  currentTokenExp,
}) => {
  track({
    type: EventTypes.LIVESTREAM_STREAM_AUTHORIZED,
    payload: {
      'stream.id': streamId,
      'stream.status': status,
      'player.preset': preset,
      'privateShow.status': privateShowStatus,
      'token.nbf': tokenNbf,
      'token.exp': tokenExp,
      'token.hasStreamToken': hasStreamToken,
      'token.currentExp': currentTokenExp,
    },
  });
};

/**
 * Handle LIVESTREAM_CHANGE_PRESET event.
 * @kind mixpanel/eventType
 * @name _liveStreamChangePreset
 * @param {string} {streamId} - livestream id.
 * @param {string} {preset} - livestream preset.
 * @param {string} {reason} - change preset reason.
 * @param {boolean} {hasToken} - has stream token.
 * @param {number} {clientTimestamp} - client timestamp.
 * @param {number} {serverTimestamp} - server timestamp.
 * @param {number} {tokenNbf} - livestream token nbf.
 * @param {number} {tokenExp} - livestream token exp.
 * @param {number} {byteplusTokenNbf} - livestream byteplus token nbf.
 * @param {number} {byteplusTokenExp} - livestream byteplus token exp.
 */
const _liveStreamChangePreset = ({
  streamId,
  preset,
  reason,
  hasToken,
  clientTimestamp,
  serverTimestamp,
  tokenNbf,
  tokenExp,
  byteplusTokenNbf,
  byteplusTokenExp,
}) => {
  track({
    type: EventTypes.LIVESTREAM_CHANGE_PRESET,
    payload: {
      'stream.id': streamId,
      'player.preset': preset,
      'preset.reason': reason,
      'token.hasToken': hasToken,
      'token.nbf': tokenNbf,
      'token.exp': tokenExp,
      'byteplusToken.nbf': byteplusTokenNbf,
      'byteplusToken.exp': byteplusTokenExp,
      'time.server': serverTimestamp,
      'time.client': clientTimestamp,
      'time.offsetSeconds': getTimeOffsetSeconds(
        serverTimestamp,
        clientTimestamp
      ),
    },
  });
};

/**
 * Handle LIVESTREAM_TOKEN_GOT event.
 * @kind mixpanel/eventType
 * @name _liveStreamTokenGot
 * @param {string} {streamId} - livestream id.
 * @param {string} {sessionId} - livestream session id.
 * @param {string} {status} - livestream status.
 * @param {number} {tokenNbf} - livestream token nbf.
 * @param {number} {tokenExp} - livestream token exp.
 * @param {number} {byteplusTokenNbf} - livestream byteplus token nbf.
 * @param {number} {byteplusTokenExp} - livestream byteplus token exp.
 * @param {string} {playerStatus} - livestream player status.
 * @param {string} {preset} - livestream preset.
 * @param {number} {clientTimestamp} - client timestamp.
 * @param {number} {serverTimestamp} - server timestamp.
 * @param {string} {via} - token coming from
 */
const _liveStreamTokenGot = ({
  streamId,
  sessionId,
  status,
  tokenNbf,
  tokenExp,
  playerStatus,
  preset,
  clientTimestamp,
  serverTimestamp,
  via,
}) => {
  track({
    type: EventTypes.LIVESTREAM_TOKEN_GOT,
    payload: {
      'stream.id': streamId,
      'stream.sessionId': sessionId,
      'stream.status': status,
      'token.nbf': tokenNbf,
      'token.exp': tokenExp,
      'token.duration': tokenExp - tokenNbf,
      'token.via': via,
      'byteplusToken.nbf': tokenNbf,
      'byteplusToken.exp': tokenExp,
      'byteplusToken.duration': tokenExp - tokenNbf,
      'player.status': playerStatus,
      'player.preset': preset,
      'time.server': serverTimestamp,
      'time.client': clientTimestamp,
      'time.offsetSeconds': getTimeOffsetSeconds(
        serverTimestamp,
        clientTimestamp
      ),
    },
  });
};
/**
 * Handle LIVESTREAM_PLAYER_FALLBACK event.
 * @kind mixpanel/eventType
 * @name _liveStreamPlayerFallback
 * @param {string} {streamId} - livestream id.
 * @param {string} {status} - livestream status.
 * @param {number} {tokenNbf} - livestream token nbf.
 * @param {number} {tokenExp} - livestream token exp.
 * @param {string} {playerStatus} - livestream player status.
 * @param {string} {preset} - livestream preset.
 * @param {string} {errorMessage} - player error message.
 */
const _liveStreamPlayerFallback = payload => {
  let finalizedPayload;
  if (payload?.usingPlayer === 'rtm') {
    const { streamId, usingPlayer, rtmUrl, rtmFallbackUrl, fallbackReason } =
      payload;
    finalizedPayload = {
      'stream.id': streamId,
      'stream.player': usingPlayer,
      'stream.rtmUrl': rtmUrl,
      'stream.rtmFallbackUrl': rtmFallbackUrl,
      'stream.fallbackReason': fallbackReason,
    };
  } else {
    const {
      streamId,
      status,
      tokenNbf,
      tokenExp,
      playerStatus,
      preset,
      errorMessage,
    } = payload;

    finalizedPayload = {
      'stream.id': streamId,
      'stream.status': status,
      'token.nbf': tokenNbf,
      'token.exp': tokenExp,
      'player.status': playerStatus,
      'player.preset': preset,
      'error.message': errorMessage,
    };
  }

  track({
    type: EventTypes.LIVESTREAM_PLAYER_FALLBACK,
    payload: finalizedPayload,
  });
};

/**
 * Handle LIVESTREAM_PLAYER_BUFFER_STALLED event.
 * @kind mixpanel/eventType
 * @name _liveStreamPlayerBufferStalled
 * @param {string} {streamId} - livestream id.
 * @param {string} {sessionId} - session id
 * @param {string} {preset} - livestream preset
 * @param {string} {source} - player source
 * @param {string} {streamStatus} - livestream status
 * @param {string} {mediaType} - Buffer stalled media type
 * @param {object} {avgBufferLength} - livestream average buffer length
 * @param {[number]} {bufferHistory} - complete buffer history
 * @param {object} {avgThroughput} - livestream average throughput
 * @param {[number]} {throughputHistory} - Throughput history
 * @param {[object]} {ManifestRepeatHistory} - repeat manifest history
 * @param {number} {tokenNbf} - stream token nbf
 * @param {number} {tokenExp} - stream token exp
 * @param {boolean} {enabledLowLatency} - low latency enabled
 * @param {string} {fundingId} - funding id
 * @param {string} {performingId} - performing id
 */
const _liveStreamPlayerBufferStalled = payload => {
  let finalizedPayload;

  if (payload?.usingPlayer === 'rtm') {
    const { streamId, usingPlayer, rtmUrl, event } = payload;
    finalizedPayload = {
      'stream.id': streamId,
      'stream.player': usingPlayer,
      'stream.rtmUrl': rtmUrl,
      'stream.event': event,
    };
  } else if (payload?.usingPlayer === 'rtc') {
    const { usingPlayer, from, streamId, sourceId, roomId, userId, event } =
      payload;
    finalizedPayload = {
      'stream.id': streamId,
      'stream.from': from,
      'stream.player': usingPlayer,
      'stream.sourceId': sourceId,
      'stream.roomId': roomId,
      'stream.userId': userId,
      'stream.event': event,
    };
  } else {
    const {
      streamId,
      sessionId,
      streamStatus,
      preset,
      source,
      mediaType,
      avgBufferLength,
      bufferHistory,
      avgThroughput,
      throughputHistory,
      manifestRepeatHistory,
      tokenNbf,
      tokenExp,
      enabledLowLatency,
      fundingId,
      performingId,
    } = payload;
    finalizedPayload = {
      'stream.id': streamId,
      'stream.sessionId': sessionId,
      'stream.status': streamStatus,
      'player.preset': preset,
      'player.source': source,
      'stream.lowLatency.enabled': enabledLowLatency,
      'stream.event.mediaType': mediaType,
      'stream.avgBufferLength': avgBufferLength,
      'stream.bufferHistory': bufferHistory,
      'stream.avgThroughput': avgThroughput,
      'stream.throughputHistory': throughputHistory,
      'stream.manifestRepeatHistory': manifestRepeatHistory,
      'token.current.nbf': tokenNbf,
      'token.current.exp': tokenExp,
      'privateShow.fundingId': fundingId,
      'privateShow.performingId': performingId,
    };
  }

  track({
    type: EventTypes.LIVESTREAM_PLAYER_BUFFER_STALLED,
    payload: finalizedPayload,
  });
};

/**
 * Handle LIVESTREAM_PLAYER_AUTOPLAY_FAILED event.
 * @kind mixpanel/eventType
 * @name _liveStreamPlayerAutoplayFailed
 * @param {Object} payload - The event payload
 * @param {string} payload.streamId - livestream id
 * @param {string} payload.usingPlayer - player type (rtc, rtm)
 * @param {string} payload.creator - creator of the stream
 * @param {string} payload.sourceId - source id
 * @param {string} payload.roomId - room id
 * @param {string} payload.userId - user id
 * @param {boolean} payload.success - whether autoplay retry was successful
 * @param {Object} payload.event - event details
 */
const _liveStreamPlayerAutoplayFailed = payload => {
  const {
    streamId,
    usingPlayer,
    creator,
    sourceId,
    roomId,
    userId,
    success,
    event,
  } = payload;

  track({
    type: EventTypes.LIVESTREAM_PLAYER_AUTOPLAY_FAILED,
    payload: {
      'stream.id': streamId,
      'stream.player': usingPlayer,
      'stream.creator': creator,
      'stream.sourceId': sourceId,
      'stream.roomId': roomId,
      'stream.userId': userId,
      'stream.success': success,
      'stream.event': event,
    },
  });
};

/**
 * Handle LIVESTREAM_PLAYER_WAITING event.
 * @kind mixpanel/eventType
 * @name _liveStreamPlayerPlayerWaiting
 * @param {string} {streamId} - livestream id.
 * @param {string} {source} - player source
 * @param {string} {sessionId} - session id
 * @param {string} {preset} - livestream preset
 * @param {string} {streamStatus} - livestream status
 * @param {string} {viewId} - mixpanel view id
 * @param {string} {usingPlayer} - player type (e.g., 'agora', 'tencent')
 * @param {number} {duration} - duration of waiting
 * @param {object} {avgBufferLength} - livestream average buffer length
 * @param {[number]} {bufferHistory} - complete buffer history
 * @param {object} {avgThroughput} - livestream average throughput
 * @param {[number]} {throughputHistory} - Throughput history
 * @param {[object]} {ManifestRepeatHistory} - repeat manifest history
 * @param {number} {tokenNbf} - stream token nbf
 * @param {number} {tokenExp} - stream token exp
 * @param {boolean} {enabledLowLatency} - low latency enabled
 * @param {string} {fundingId} - funding id
 * @param {string} {performingId} - performing id
 */
const _liveStreamPlayerPlayerWaiting = payload => {
  let finalizedPayload;

  if (payload?.usingPlayer === 'rtm') {
    const { usingPlayer, streamId, creator, isMuted, rtmUrl } = payload;
    finalizedPayload = {
      'stream.id': streamId,
      'stream.player': usingPlayer,
      'stream.rtmUrl': rtmUrl,
      'user.username': creator,
      'stream.muted': isMuted,
    };
  }
  if (payload?.usingPlayer === 'rtc') {
    const {
      usingPlayer,
      streamId,
      creator,
      isMuted,
      appId,
      roomId,
      roomToken,
      roomProfileType,
      rtcRemoteVideoConfigWidth,
      rtcRemoteVideoConfigHeight,
      sourceId,
      userId,
      fundingId,
      performingId,
    } = payload;
    finalizedPayload = {
      'stream.id': streamId,
      'stream.player': usingPlayer,
      'stream.appId': appId,
      'stream.roomId': roomId,
      'stream.roomToken': roomToken,
      'stream.roomProfileType': roomProfileType,
      'stream.remoteVideoConfigWidth': rtcRemoteVideoConfigWidth,
      'stream.remoteVideoConfigHeight': rtcRemoteVideoConfigHeight,
      'stream.sourceId': sourceId,
      'stream.userId': userId,
      'stream.muted': isMuted,
      'user.username': creator,
      'privateShow.fundingId': fundingId,
      'privateShow.performingId': performingId,
    };
  } else {
    const {
      streamId,
      sessionId,
      streamStatus,
      preset,
      source,
      duration,
      avgBufferLength,
      bufferHistory,
      avgThroughput,
      throughputHistory,
      manifestRepeatHistory,
      tokenNbf,
      tokenExp,
      enabledLowLatency,
      fundingId,
      performingId,
      viewId,
      usingPlayer,
    } = payload;
    finalizedPayload = {
      'stream.id': streamId,
      'stream.player': usingPlayer,
      'stream.sessionId': sessionId,
      'stream.status': streamStatus,
      'player.preset': preset,
      'player.source': source,
      'stream.lowLatency.enabled': enabledLowLatency,
      duration: duration,
      'stream.avgBufferLength': avgBufferLength,
      'stream.bufferHistory': bufferHistory,
      'stream.avgThroughput': avgThroughput,
      'stream.throughputHistory': throughputHistory,
      'stream.manifestRepeatHistory': manifestRepeatHistory,
      'token.current.nbf': tokenNbf,
      'token.current.exp': tokenExp,
      'privateShow.fundingId': fundingId,
      'privateShow.performingId': performingId,
      'view.id': viewId,
    };
  }

  track({
    type: EventTypes.LIVESTREAM_PLAYER_WAITING,
    payload: finalizedPayload,
  });
};

/**
 * Handle PUSHER_EVENT_GOT event.
 * @kind mixpanel/eventType
 * @name _pusherEventGot
 * @param {[string]} {eventType} - pusher event type
 * @param {[object]} {eventPayload} - pusher event payload
 */
const _pusherEventGot = ({ eventType, eventPayload }) => {
  return track({
    type: EventTypes.PUSHER_EVENT_GOT,
    payload: {
      'event.type': eventType,
      'event.payload': eventPayload,
    },
  });
};

/**
 * Handle PUSHER_CHANNEL_STALLED event.
 * @kind mixpanel/eventType
 * @name _pusherChannelStalled
 * @param {string} {channelName} - channel name
 * @param {boolean} {subscribed} - channel subscribed
 * @param {boolean} {subscriptionCanceled} - channel subscription canceled
 * @param {boolean} {subscriptionPending} - channel subscription pending
 * @param {string} {connectionState} - pusher connection state
 */
const _pusherChannelStalled = ({
  channelName,
  subscribed,
  subscriptionCancelled,
  subscriptionPending,
  connectionState,
}) => {
  return track({
    type: EventTypes.PUSHER_CHANNEL_STALLED,
    payload: {
      'channel.name': channelName,
      'channel.subscribed': subscribed,
      'channel.subscriptionCancelled': subscriptionCancelled,
      'channel.subscriptionPending': subscriptionPending,
      'connection.state': connectionState,
    },
  });
};

/**
 * Handle USER_DEBUG_SENT event.
 * @kind mixpanel/eventType
 * @name _userDebugSent
 * @param {object} {[drmTestResult]} - drm test result.
 * @param {string} {[drmTestMessageId]} - drm test message id.
 * @param {object} {speedtestResult} - speed test result.
 * @param {object} {pusherStatsResult} - pusher connection result.
 * @param {object} {[configRawData]} - raw data in operation.config.
 * @param {object} {[abRawData]} - raw data in me.ab.
 * @param {object} {[tokens]} - raw data in me.jwtData, me.refreshTokenJwtData.
 * @param {string} {[fcmToken]} - raw data in me.fcmToken.
 */
const _userDebugSent = ({
  drmTestResult = {},
  drmTestMessageId,
  speedtestResult = {},
  pusherStatsResult = {},
  configRawData,
  abRawData,
  tokens,
  fcmToken,
  fingerprintData = {},
}) => {
  const payload = {};

  if (drmTestResult) {
    payload['drm.message.id'] = drmTestMessageId;
  }

  Object.keys(DrmRobustnessPriority).forEach(keySystem => {
    const regexp = /[^a-zA-Z0-9_]/g;
    const replacedKeySystem = keySystem.replace(regexp, '_').toLowerCase();
    Object.keys(DrmRobustnessPriority[keySystem]).forEach(robustness => {
      const replacedRobustness = robustness.replace(regexp, '_').toLowerCase();
      payload[`drm.${replacedKeySystem}.${replacedRobustness}`] =
        drmTestResult?.[keySystem]?.[robustness] || 'untest';
    });
  });
  Object.keys(speedtestResult).forEach(key => {
    // format to underscore case
    const formattedKey = key
      .split(/(?=[A-Z])/)
      .join('_')
      .toLowerCase();
    payload[`speedtest.${formattedKey}`] = speedtestResult[key];
  });
  Object.keys(pusherStatsResult).forEach(key => {
    // format to underscore case
    const formattedKey = key
      .split(/(?=[A-Z])/)
      .join('_')
      .toLowerCase();
    payload[`pusher.stats.${formattedKey}`] = pusherStatsResult[key];
  });

  if (configRawData) {
    payload['config.raw'] = configRawData;
  }

  if (abRawData) {
    payload['token.ab.raw'] = abRawData;
  }

  if (tokens) {
    Object.entries(tokens).forEach(([key, value]) => {
      const tokenFields = ['scopes', 'exp', 'nbf', 'iat'];
      const tokenKey = `token.${key}`;

      // make sure payload[tokenKey] is an object
      payload[tokenKey] = payload[tokenKey] || {};

      tokenFields.forEach(field => {
        if (value?.[field]) {
          payload[tokenKey][field] = value[field];
        }
      });
    });
  }

  if (fcmToken) {
    payload['token.fcmToken'] = fcmToken;
  }

  Object.entries(fingerprintData).forEach(([key, value]) => {
    payload[`fingerprint.${key}`] = value;
  });

  if (getIsOnWebviewApp()) {
    if (window.WebApp) {
      Object.entries(window.WebApp).forEach(([key, value]) => {
        try {
          payload[`webviewapp.${key}`] = JSON.stringify(value);
        } catch (error) {
          payload[`webviewapp.${key}`] = `stringify error: ${error}`;
        }
      });
    } else {
      payload['webviewapp'] = 'window.WebApp is not defined';
    }
  }

  return track({
    type: EventTypes.USER_DEBUG_SENT,
    payload,
  });
};

/**
 * Handle USER_LOGGED_IN event.
 * @kind mixpanel/eventType
 * @name _userLoggedIn
 * @param {string} {via} - user login via
 */
const _userLoggedIn = ({ via }) => {
  return track({
    type: EventTypes.USER_LOGGED_IN,
    payload: {
      via,
    },
  });
};

/**
 * Handle MESSAGE_CREATE_FAILED and MESSAGE_UPDATE_FAILED event.
 * @kind mixpanel/eventType
 * @name _messageFailed
 */
const _messageFailed = ({ type, error, request }) => {
  return track({
    type,
    payload: {
      request,
      error,
    },
  });
};

const _orderCreationSuccess = ({
  id,
  amount,
  currency,
  email,
  items = [],
  source = {},
  sources = [],
}) => {
  return track({
    type: EventTypes.ORDER_CREATION_SUCCESS,
    payload: {
      'order.id': id,
      'order.price': amount,
      'order.currency': currency,
      'order.email': email,
      'order.items': items,
      'order.source': source,
      'order.sources': sources,
    },
  });
};

const _orderCreationFailed = ({
  error,
  skus,
  email,
  currency,
  source = {},
}) => {
  return track({
    type: EventTypes.ORDER_CREATION_FAILED,
    payload: {
      'order.error': error,
      'order.skus': skus,
      'order.email': email,
      'order.currency': currency,
      'order.source': source,
    },
  });
};

/**
 * Handle USER_WEB_VITALS_METRIC_SENT event.
 * @kind mixpanel/eventType
 * @name _userWebVitalsMetricSent
 * @param {string} {name} - web vitals name
 * @param {number} {value} - web vitals value
 * @param {string} {navigationType} - navigation type
 * @param {string} {rating} - rating
 */
const _userWebVitalsMetricSent = ({ name, value, navigationType, rating }) => {
  return track({
    type: EventTypes.USER_WEB_VITALS_METRIC_SENT,
    payload: {
      'metric.name': name,
      'metric.value': value,
      'metric.navigationType': navigationType,
      'metric.rating': rating,
    },
  });
};

/**
 * Handle USER_DEBUG_FEED_CONTENT_ERROR event.
 * @kind mixpanel/eventType
 * @name _userDebugFeedContentError
 * @param {object} {payload} - mixpanel payload
 */
const _userDebugFeedContentError = payload => {
  return track({
    type: EventTypes.USER_DEBUG_FEED_CONTENT_ERROR,
    payload,
  });
};

/**
 * Handle USER_DEBUG_NETWORKING_REQUEST event.
 * @kind mixpanel/eventType
 * @name _userDebugNetworkingRequest
 * @param {object} {payload} - mixpanel payload
 * @param {object} {payload.request} - request object.
 * @param {string} {payload.request.id} - request id.
 * @param {string} {payload.request.url} - request url.
 * @param {object} {payload.response} - response object.
 * @param {number} {payload.response.status} - response status.
 * @param {string} {payload.response.body} - response body.
 * @param {string} {payload.response.error} - response error message.
 */
const _userDebugNetworkingRequest = payload => {
  return track({
    type: EventTypes.USER_DEBUG_NETWORKING_REQUEST,
    payload: {
      'request.id': payload.request?.id,
      'request.url': payload.request?.url,
      'response.status': payload.response?.status,
      'response.body': payload.response?.body,
      'response.error': payload.response?.error,
    },
  });
};

/**
 * Handle USER_DEBUG_VERSION_CHANGED event.
 * @kind mixpanel/eventType
 * @name _userDebugVersionChanged
 * @param {object} {payload} - mixpanel payload
 * @param {string} {payload.version} - version
 * @param {string} {payload.shortSha} - shortSha
 * @param {string} {payload.versionFromMetaTag} - versionFromMetaTag
 * @param {string} {payload.shortShaFromMetaTag} - shortShaFromMetaTag
 */
const _userDebugVersionChanged = payload => {
  return track({
    type: EventTypes.USER_DEBUG_VERSION_CHANGED,
    payload: {
      'version.from.api': payload.version,
      'version.from.meta': payload.versionFromMetaTag,
      'shortSha.from.api': payload.shortSha,
      'shortSha.from.meta': payload.shortShaFromMetaTag,
    },
  });
};

/**
 * Handle USER_DEBUG_FCM_TOKEN event.
 * @kind mixpanel/eventType
 * @name _userDebugFcmToken
 * @param {object} {payload} - mixpanel payload
 * @param {string} {payload.fcmToken} - fcm token
 */
const _userDebugFcmToken = payload => {
  let finalPayload;

  if (payload.from === 'PushNotificationHandler') {
    const {
      from,
      via,
      hasPushManager,
      prevPermission,
      browserPermission,
      storedPermission,
    } = payload;
    finalPayload = {
      'fcm.from': from,
      'fcm.via': via,
      'fcm.hasPushManager': hasPushManager,
      'fcm.prevPermission': prevPermission,
      'fcm.browserPermission': browserPermission,
      'fcm.storedPermission': storedPermission,
    };
  } else if (payload.from === 'setFcmToken') {
    const {
      isAuthed,
      browserPermission,
      storedPermission,
      hasPushManager,
      hasServiceWorker,
      via,
      fcmToken,
      error,
    } = payload;
    finalPayload = {
      'fcm.from': payload.from,
      'fcm.via': via,
      'fcm.isAuthed': isAuthed,
      'fcm.hasPushManager': hasPushManager,
      'fcm.browserPermission': browserPermission,
      'fcm.storedPermission': storedPermission,
      'fcm.hasServiceWorker': hasServiceWorker,
      'fcm.token': fcmToken,
      'fcm.error': error,
    };
  } else if (payload.from === 'submitPushNotificationToken') {
    const { fcmToken, pushyToken, error } = payload;
    finalPayload = {
      'fcm.from': payload.from,
      'fcm.token': fcmToken,
      'fcm.pushyToken': pushyToken,
      'fcm.error': error,
    };
  }

  return track({
    type: EventTypes.USER_DEBUG_FCM_TOKEN,
    payload: finalPayload,
  });
};

/**
 * Handle USER_DEBUG_MESSAGE_PRICE event.
 * @kind mixpanel/eventType
 * @name _userDebugMessagePrice
 * @param {object} {payload} - mixpanel payload
 * @param {string} {payload.messageId} - message id.
 * @param {number} {payload.price} - price.
 */
const _userDebugMessagePrice = payload => {
  return track({
    type: EventTypes.USER_DEBUG_MESSAGE_PRICE,
    payload,
  });
};

/**
 * Handle USER_DEBUG_PLAYER_ERROR event.
 * @kind mixpanel/eventType
 * @name _userDebugPlayerError
 * @param {object} {payload} - mixpanel payload
 */
const _userDebugPlayerError = payload => {
  return track({
    type: EventTypes.USER_DEBUG_PLAYER_ERROR,
    payload,
  });
};

/**
 * Handle USER_TOKEN_GOT event.
 * @kind mixpanel/eventType
 * @name _userTokenGot
 * @param {object} {payload} - mixpanel payload
 * @param {object} {payload.token} - token object.
 * @param {string} {payload.token.type} - token type.
 * @param {string} {payload.token.via} - token via.
 * @param {array} {payload.token.scopes} - token scopes.
 * @param {number} {payload.token.iat} - token iat.
 * @param {number} {payload.token.exp} - token exp.
 */
const _userTokenGot = payload => {
  return track({
    type: EventTypes.USER_TOKEN_GOT,
    payload: {
      'token.type': payload.token?.type,
      'token.via': payload.token?.via,
      'token.scopes': payload.token?.scopes,
      'token.iat': payload.token?.iat,
      'token.exp': payload.token?.exp,
    },
  });
};

/**
 * Handle FEED_FILTER_APPLIED event.
 * @kind mixpanel/eventType
 * @name _feedFilterApplied
 * @param {object} {payload} - mixpanel payload
 * @param {array} {payload.filterOptions} - filter options.
 * @param {number} {payload.totalCounts} - total counts.
 */
const _feedFilterApplied = payload => {
  return track({
    type: EventTypes.FEED_FILTER_APPLIED,
    payload: {
      'filter.options': payload.filterOptions.join(', '),
      'filter.totalCounts': payload.totalCounts,
    },
  });
};

/**
 * Handle PLAYER_ERROR event.
 * @kind mixpanel/eventType
 * @name _playerError
 * @param {string} {errorCode} - error code
 * @param {string} {errorMessage} - error message
 * @param {string} {errorReason} - error reason
 * @param {object} {extraProps} - extra props
 */
const _playerError = ({
  errorCode,
  errorMessage,
  errorReason,
  extraProps = {},
}) => {
  return track({
    type: EventTypes.PLAYER_ERROR,
    payload: {
      'error.code': errorCode,
      'error.message': errorMessage,
      'error.reason': errorReason,
      ...extraProps,
    },
  });
};

/**
 * Handle AUDIO_PLAYER_ERROR event (voice message playback failures).
 * @kind mixpanel/eventType
 * @name _audioPlayerError
 * @param {object} payload
 * @param {string} payload.errorReason - where/why the audio player failed
 * @param {string|number} [payload.errorCode] - player error code
 * @param {string} [payload.errorMessage] - player error message
 * @param {string} [payload.messageId] - chat message id that failed to play
 * @param {string} [payload.errorData] - serialized extra error context
 * @param {boolean} [payload.isErrorUndefined] - whether the error object was missing
 */
const _audioPlayerError = ({
  errorReason,
  errorCode,
  errorMessage,
  messageId,
  errorData,
  isErrorUndefined,
}) => {
  return track({
    type: EventTypes.AUDIO_PLAYER_ERROR,
    payload: {
      'error.reason': errorReason,
      'error.code': errorCode,
      'error.message': errorMessage,
      'error.data': errorData,
      messageId,
      isErrorUndefined,
    },
  });
};

export const mixpanelStartBatchSenders = () => {
  mixpanelLog.extend('mixpanelStartBatchSenders')('start batch senders');
  return window.mixpanel?.start_batch_senders();
};

export const mixpanelStartSessionRecording = () => {
  mixpanelLog('mixpanelStartSessionRecording');
  window.mixpanel?.start_session_recording();
};

export const mixpanelStopSessionRecording = () => {
  mixpanelLog('mixpanelStopSessionRecording');
  window.mixpanel?.stop_session_recording();
};

export const initMixpanel = async ({
  pubSubPrefix,
  pubSubPostfix,
  pubSubTopic,
  replayConfig,
  initEvent = 'load',
} = {}) => {
  const log = mixpanelLog.extend('initMixpanel');
  log('init');

  if (!window.mixpanel) {
    if (!pubSubController) {
      const PubSubController = (await import('../resource/PubSubController.js'))
        .default;
      pubSubController = new PubSubController({
        pubSubTopic,
        pubSubPrefix,
        pubSubPostfix,
      });
    }

    if (!replayController && replayConfig) {
      const MixpanelReplayController = (
        await import('../resource/mixpanelReplayController.js')
      ).default;

      replayController = new MixpanelReplayController({ config: replayConfig });

      window._replayController = replayController;
    }

    const { pipeMixpanelEventToHotjar } = await import('../resource/hotjar.js');

    window.MIXPANEL_CUSTOM_LIB_URL = env.MIXPANEL_CUSTOM_LIB_URL;
    await loadScriptTag({
      id: 'mixpanel-script',
      async: false,
      textContent: MIXPANEL_SCRIPT,
    });

    let debug = false;
    try {
      debug =
        new URLSearchParams(window.location.search).get('debug-mixpanel') ===
        '1';
    } catch (_) {
      //
    }

    return new Promise(resolve => {
      const firstEventDelay = +(env.MIXPANEL_FIRST_EVENT_DELAY_MSEC || 10000);
      const flushInterval = +(env.MIXPANEL_BATCH_FLUSH_INTERVAL_MS || 5000);
      window.mixpanel.init(env.MIXPANEL_TOKEN, {
        debug,
        loaded: () => {
          log('done init mixpanel');
          // https://github.com/mixpanel/mixpanel-js/blob/master/src/mixpanel-core.js#L773-L777
          sanitizeStorage({
            queryKey: '__mpq_' + window.mixpanel.get_config('token') + '_ev',
          });

          trackAvailability();

          if (initEvent.includes('load')) {
            setTimeout(() => {
              log(`after ${firstEventDelay}s start_batch_senders`);
              window.mixpanel.start_batch_senders();
            }, firstEventDelay);
          }

          mixpanelApiCallQueue.forEach(payload =>
            processMixpanelApiCallCore(payload)
          );
          mixpanelApiCallQueue = [];

          resolve();
        },
        // Hooks run on every flush, reading env at invocation time (not init time).
        // env is a mutable object updated by updateEnv() when remote config changes,
        // so toggling FEATURE_SEND_*_TO_MIXPANEL takes effect without re-init.
        // Side-effects (PubSub, Replay, Hotjar) execute before the toggle check
        // and are unaffected by the remote config value.
        hooks: {
          before_send_events: function (event) {
            const { event: sanitizedEvent } = sanitizeEvent(event);

            const insertId = sanitizedEvent?.properties?.['$insert_id'];
            // Events without $insert_id are always forwarded (no dedup possible).
            if (!insertId || !pubSubSentInsertIds.has(insertId)) {
              // An empty sanitized event (falsy or empty object) means the raw
              // event came in empty upstream. Report the original event to
              // Sentry so we can inspect what produced it. setFingerprint groups
              // every report into a single issue to avoid flooding.
              if (isEmpty(sanitizedEvent)) {
                getSentry().then(({ withScope, captureMessage } = {}) => {
                  if (!withScope || !captureMessage) return;
                  withScope(scope => {
                    let rawEvent = '**unserializable event**';
                    let sanitized = '**unserializable event**';
                    try {
                      rawEvent = JSON.stringify(event);
                    } catch (_) {
                      //
                    }
                    try {
                      sanitized = JSON.stringify(sanitizedEvent);
                    } catch (_) {
                      //
                    }
                    scope.setContext('emptyMixpanelEvent', {
                      eventType: typeof event,
                      rawEvent,
                      sanitizedEvent: sanitized,
                    });
                    scope.setFingerprint([
                      'mixpanel',
                      'empty-before-send-event',
                    ]);
                    captureMessage(
                      'before_send_events received empty event',
                      Severity.Debug
                    );
                  });
                });
              }

              pubSubController?.addToQueue(sanitizedEvent);
              replayController?.addToQueue(sanitizedEvent);
              pipeMixpanelEventToHotjar(sanitizedEvent);
              if (insertId) {
                if (pubSubSentInsertIds.size >= MAX_DEDUP_SIZE) {
                  const DEDUP_BUFFER = 5;
                  const deleteCount =
                    pubSubSentInsertIds.size - MAX_DEDUP_SIZE + DEDUP_BUFFER;
                  const oldestInsertIds = pubSubSentInsertIds.values();
                  for (let i = 0; i < deleteCount; i++) {
                    pubSubSentInsertIds.delete(oldestInsertIds.next().value);
                  }
                }
                pubSubSentInsertIds.add(insertId);
              }
            }

            if (!env.FEATURE_SEND_EVENT_TO_MIXPANEL) {
              log('before_send_events blocked');
              // Return empty object so SDK still sends the request and dequeues the item.
              // Returning null or falsy value would skip the request, leaving items in the queue permanently
              // and causing repeated flush cycles with duplicated side-effects.
              return {};
            }
            return sanitizedEvent;
          },
          before_send_people: function (people) {
            pubSubController?.addToQueue(people);

            if (!env.FEATURE_SEND_PEOPLE_TO_MIXPANEL) {
              log('before_send_people blocked');
              // Return empty object so SDK still sends the request and dequeues the item.
              // Returning null or falsy value would skip the request, leaving items in the queue permanently
              // and causing repeated flush cycles with duplicated side-effects.
              return {};
            }
            return people;
          },
        },
        api_transport: 'sendBeacon',
        batch_flush_interval_ms: flushInterval,
        // set value to false, and the events won't be sent until invoking start_batch_senders
        batch_autostart: false,
        record_idle_timeout_ms: +env.MIXPANEL_RECORD_IDLE_TIMEOUT_MS,
        record_max_ms: +env.MIXPANEL_RECORD_MAX_MS,
        record_mask_text_selector: env.MIXPANEL_RECORD_MASK_TEXT_SELECTOR,
      });
    });
  }
  return window.mixpanel;
};

let uuid;
const MIXPANEL_DEVICE_ID_PREFIX = '$device:';
export const getMixpanelDistinctId = ({ reload = false } = {}) => {
  // reload the sync the lastest distinct id from other tabs
  if (reload) processMixpanelApiCall({ apiNames: ['persistence', 'load'] });
  let distinctId =
    processMixpanelApiCall({ apiNames: ['get_distinct_id'] }) || uuid;

  if (getIsOnWebviewApp()) {
    const url = new URL(window.location.href);
    const distinctIdFromQuery = url.searchParams.get('mp_distinct_id');

    // before user login or after user logout, the distinct id always starts with '$device:'
    // so if the distinct id is not set, or the distinct id starts with '$device:', then use the distinct id from query
    if (
      (!distinctId || distinctId.startsWith(MIXPANEL_DEVICE_ID_PREFIX)) &&
      distinctIdFromQuery
    ) {
      distinctId = distinctIdFromQuery;
      processMixpanelApiCall({
        apiNames: ['register'],
        args: [{ distinct_id: distinctId }],
      });

      url.searchParams.delete('mp_distinct_id');
      window.history.replaceState(null, '', url.href);
    }
  }

  if (!distinctId) {
    uuid = uuidv4();
    distinctId = uuid;

    processMixpanelApiCall({
      apiNames: ['register'],
      args: [{ distinct_id: uuid }],
    });
  }

  return distinctId;
};

export const updatePubSubConfig = async ({
  pubSubTopic,
  pubSubPrefix,
  pubSubPostfix,
}) => {
  if (!pubSubController) {
    const PubSubController = (await import('../resource/PubSubController.js'))
      .default;
    pubSubController = new PubSubController({
      pubSubTopic,
      pubSubPrefix,
      pubSubPostfix,
    });
  } else {
    pubSubController.updatePubSubConfig({
      pubSubTopic,
      pubSubPrefix,
      pubSubPostfix,
    });
  }
};

export const updateMixpanelReplayConfig = async config => {
  if (!replayController) {
    const MixpanelReplayController = (
      await import('../resource/mixpanelReplayController.js')
    ).default;

    replayController = new MixpanelReplayController({ config });
  } else replayController.updateConfig(config);
};

/**
 * Sanitize the storage
 * @param {object} options
 * @param {string} options.queryKey
 * @param {number} options.batchSize
 */
const sanitizeStorage = async ({ queryKey, batchSize = 50 } = {}) => {
  const log = mixpanelLog.extend('sanitizeStorage');
  const getItems = queryKey => {
    let items = [];
    try {
      items = localStorage.getItem(queryKey);
      if (items) items = JSON.parse(items);
    } catch (_) {
      //
    }
    return items;
  };

  try {
    let items = getItems(queryKey);
    if (items?.length) {
      log('start');
      const sanitizedItems = [];
      for (let i = 0; i < items.length; i += batchSize) {
        const itemsChunk = items.slice(i, i + batchSize);
        itemsChunk.forEach(item => {
          const { event: sanitizedEvent, isSanitized } = sanitizeEvent(
            item.payload
          );
          if (isSanitized) {
            item.payload = sanitizedEvent;
            sanitizedItems.push(item);
          }
        });
        // Yield to the main thread
        await new Promise(resolve => setTimeout(resolve));
      }

      // if there are dirty events and the events are still in the storage, replace them
      if (sanitizedItems.length) {
        const latestItems = getItems(queryKey);
        if (latestItems.length) {
          log('sanitize start');
          sanitizedItems.forEach(item => {
            const index = latestItems.findIndex(({ id }) => id === item.id);
            if (index !== -1) latestItems[index] = item;
          });
          localStorage.setItem(queryKey, JSON.stringify(latestItems));
          log('sanitize done');
        }
      }

      log('done');
    }
  } catch (_) {
    //
  }
};

/**
 * Sanitize the event
 * @param {object} event
 * @returns {object} isSanitized
 */
const sanitizeEvent = event => {
  const canEncode = value => {
    try {
      encodeURIComponent(window.mixpanel._.JSONEncode(value));
      return true;
    } catch (_) {
      return false;
    }
  };

  try {
    if (!canEncode(event) && event.properties) {
      const sanitizedProperties = Object.entries(event.properties).reduce(
        (acc, [key, value]) => {
          if (canEncode(value)) {
            return {
              ...acc,
              [key]: value,
            };
          }

          if (typeof value === 'string') {
            let newValue = '**unsupported property content**';
            try {
              // JSON.stringify the value to avoid mixpanel encoding error
              // and slice the fisrt 1 ~ 256 (255) chars for better reading
              newValue = JSON.stringify(value).slice(1, 256);
            } catch (_) {
              //
            }

            return {
              ...acc,
              [key]: newValue,
            };
          }

          return {
            ...acc,
            [key]: '**unsupported property content**',
          };
        },
        {}
      );

      const sanitizedEvent = {
        ...event,
        properties: sanitizedProperties,
      };

      return {
        event: sanitizedEvent,
        isSanitized: true,
      };
    }
  } catch (_) {
    //
  }
  return {
    event,
    isSanitized: false,
  };
};

/**
 * Handle LIVESTREAM_FETCH_TOKEN_START event.
 * @kind mixpanel/eventType
 * @name _liveStreamFetchTokenStart
 * @param {string} {streamId} - livestream id.
 * @param {string} {sessionId} - session id.
 * @param {string} {fetchUrl} - fetch url.
 * @param {string} {activeStreamId} - active stream id.
 * @param {string} {requestId} - request id.
 * @param {LivestreamFetchTokenPurpose} {purpose} - purpose of the fetch token.
 */
const _liveStreamFetchTokenStart = ({
  requestId,
  streamId,
  sessionId,
  fetchUrl,
  activeStreamId,
  purpose,
}) => {
  track({
    type: EventTypes.LIVESTREAM_FETCH_TOKEN_START,
    payload: {
      'stream.id': streamId,
      'stream.sessionId': sessionId,
      'stream.fetchUrl': fetchUrl,
      'stream.activeStreamId': activeStreamId,
      'request.id': requestId,
      'request.purpose': purpose,
    },
  });
};

/**
 * Handle LIVESTREAM_FETCH_TOKEN_ERROR event.
 * @kind mixpanel/eventType
 * @name _liveStreamFetchTokenError
 * @param {string} {streamId} - livestream id.
 * @param {string} {sessionId} - session id.
 * @param {number} {status} - error status.
 * @param {string} {statusText} - error status text.
 * @param {string} {error} - error message.
 * @param {string} {errorType} - error type.
 * @param {string} {requestId} - request id.
 * @param {LivestreamFetchTokenPurpose} {purpose} - purpose of the fetch token.
 */
const _liveStreamFetchTokenError = ({
  streamId,
  sessionId,
  errorMessage,
  errorStatus,
  requestId,
  purpose,
}) => {
  track({
    type: EventTypes.LIVESTREAM_FETCH_TOKEN_ERROR,
    payload: {
      'stream.id': streamId,
      'stream.sessionId': sessionId,
      'error.status': errorStatus,
      'error.message': errorMessage,
      'request.id': requestId,
      'request.purpose': purpose,
    },
  });
};

/**
 * Handle LIVESTREAM_PAYMENT_ERROR event.
 * @kind mixpanel/eventType
 * @name _liveStreamPaymentError
 * @param {object} {payload} - tracking payload.
 */
const _liveStreamPaymentError = payload => {
  track({
    type: EventTypes.LIVESTREAM_PAYMENT_ERROR,
    payload,
  });
};

/**
 * Handle LIVESTREAM_EXCLUSIVE_STATUS_CHANGE event.
 * @kind mixpanel/eventType
 * @name _liveStreamExclusiveStatusChange
 * @param {string} streamId - Unique identifier of the livestream
 * @param {string} streamStatus - Detailed status of the stream (e.g., 'PERFORMING', 'PREPARING', 'CONSIDERING')
 * @param {string} fromStatus - The status that the livestream is transitioning from
 * @param {string} nextStatus - The target status that the livestream is transitioning to
 * @param {string} activeStreamId - ID of the currently active stream if multiple streams exist
 * @param {number} clientTimestamp - Timestamp when the event was triggered on the client side
 * @param {number} serverTimestamp - Timestamp when the event was received by the server
 * @param {number} price - Price of the exclusive private livestream
 * @param {string} currentId - ID of the current goal
 * @param {string} currentGoalType - Type of the current goal (e.g., 'EXCLUSIVE', 'TRIGGER_EXCLUSIVE')
 * @param {string} lastId - ID of the previous goal
 * @param {string} lastGoalType - Type of the previous goal
 * @param {string} [currentGoalExclusiveTo] - Optional: Specific user or group the current goal is exclusive to
 * @param {string} [lastGoalExclusiveTo] - Optional: Specific user or group the previous goal was exclusive to
 * @returns {void}
 */
const _liveStreamExclusiveStatusChange = ({
  streamId,
  streamStatus,
  fromStatus,
  nextStatus,
  activeStreamId,
  clientTimestamp,
  serverTimestamp,
  price,
  currentId,
  currentGoalType,
  lastId,
  lastGoalType,
  currentGoalExclusiveTo,
  lastGoalExclusiveTo,
}) => {
  track({
    type: EventTypes.LIVESTREAM_EXCLUSIVE_STATUS_CHANGE,
    payload: {
      'stream.id': streamId,
      'stream.streamStatus': streamStatus,
      'stream.activeStreamId': activeStreamId,
      'stream.exclusive.price': price,
      'stream.exclusive.fromStatus': fromStatus,
      'stream.exclusive.toStatus': nextStatus,
      'stream.exclusive.currentGoalId': currentId,
      'stream.exclusive.currentGoalType': currentGoalType,
      'stream.exclusive.currentGoalExclusiveTo': currentGoalExclusiveTo,
      'stream.exclusive.lastGoalId': lastId,
      'stream.exclusive.lastGoalType': lastGoalType,
      'stream.exclusive.lastGoalExclusiveTo': lastGoalExclusiveTo,
      'time.server': serverTimestamp,
      'time.client': clientTimestamp,
      'time.offsetSeconds': getTimeOffsetSeconds(
        serverTimestamp,
        clientTimestamp
      ),
    },
  });
};

/**
 * Handle a viewer camera preview failure.
 * @kind mixpanel/eventType
 * @name _livestreamViewerCameraPreviewFailed
 * @param {object} payload
 * @param {string} payload.streamId - Stream id.
 * @param {number} payload.clientTimestamp - Client timestamp.
 * @param {string} payload.trigger - Preview trigger.
 * @param {string} [payload.step] - Failed lifecycle step.
 * @param {string} [payload.errorName] - Error name.
 * @param {string} [payload.errorMessage] - Error message.
 * @param {string} [payload.trackType] - Track that failed to capture.
 * @param {boolean} [payload.isPartial] - Whether another track was captured.
 * @returns {void}
 */
const _livestreamViewerCameraPreviewFailed = ({
  streamId,
  clientTimestamp,
  trigger,
  step,
  errorName,
  errorMessage,
  trackType,
  isPartial,
}) => {
  track({
    type: EventTypes.LIVESTREAM_VIEWER_CAMERA_PREVIEW_FAILED,
    payload: {
      'stream.id': streamId,
      'time.client': clientTimestamp,
      'viewerCamera.trigger': trigger,
      'viewerCamera.failStep': step,
      'viewerCamera.errorName': errorName,
      'viewerCamera.errorMessage': errorMessage,
      'viewerCamera.trackType': trackType,
      'viewerCamera.isPartial': isPartial,
    },
  });
};

/**
 * Handle a viewer camera publish attempt.
 * @kind mixpanel/eventType
 * @name _livestreamViewerCameraPublishAttempt
 * @param {object} payload
 * @param {string} payload.streamId - Stream id.
 * @param {number} payload.clientTimestamp - Client timestamp.
 * @returns {void}
 */
const _livestreamViewerCameraPublishAttempt = ({
  streamId,
  clientTimestamp,
}) => {
  track({
    type: EventTypes.LIVESTREAM_VIEWER_CAMERA_PUBLISH_ATTEMPT,
    payload: {
      'stream.id': streamId,
      'time.client': clientTimestamp,
    },
  });
};

/**
 * Handle a successful viewer camera publish.
 * @kind mixpanel/eventType
 * @name _livestreamViewerCameraPublishSucceeded
 * @param {object} payload
 * @param {string} payload.streamId - Stream id.
 * @param {number} payload.clientTimestamp - Client timestamp.
 * @param {string} payload.channelId - Agora channel id.
 * @param {number} payload.msFromAttempt - Time since the publish attempt.
 * @param {boolean} payload.hasVideo - Whether video was published.
 * @param {boolean} payload.hasAudio - Whether audio was published.
 * @returns {void}
 */
const _livestreamViewerCameraPublishSucceeded = ({
  streamId,
  clientTimestamp,
  channelId,
  msFromAttempt,
  hasVideo,
  hasAudio,
}) => {
  track({
    type: EventTypes.LIVESTREAM_VIEWER_CAMERA_PUBLISH_SUCCEEDED,
    payload: {
      'stream.id': streamId,
      'time.client': clientTimestamp,
      'viewerCamera.channelId': channelId,
      'viewerCamera.msFromAttempt': msFromAttempt,
      'viewerCamera.hasVideo': hasVideo,
      'viewerCamera.hasAudio': hasAudio,
    },
  });
};

/**
 * Handle a viewer camera publish failure.
 * @kind mixpanel/eventType
 * @name _livestreamViewerCameraPublishFailed
 * @param {object} payload
 * @param {string} payload.streamId - Stream id.
 * @param {number} payload.clientTimestamp - Client timestamp.
 * @param {number} payload.msFromAttempt - Time since the publish attempt.
 * @param {string} [payload.step] - Failed lifecycle step.
 * @param {string} [payload.errorName] - Error name.
 * @param {string} [payload.errorMessage] - Error message.
 * @returns {void}
 */
const _livestreamViewerCameraPublishFailed = ({
  streamId,
  clientTimestamp,
  msFromAttempt,
  step,
  errorName,
  errorMessage,
}) => {
  track({
    type: EventTypes.LIVESTREAM_VIEWER_CAMERA_PUBLISH_FAILED,
    payload: {
      'stream.id': streamId,
      'time.client': clientTimestamp,
      'viewerCamera.msFromAttempt': msFromAttempt,
      'viewerCamera.failStep': step,
      'viewerCamera.errorName': errorName,
      'viewerCamera.errorMessage': errorMessage,
    },
  });
};

/**
 * Handle the end of viewer camera publishing.
 * @kind mixpanel/eventType
 * @name _livestreamViewerCameraPublishStopped
 * @param {object} payload
 * @param {string} payload.streamId - Stream id.
 * @param {number} payload.clientTimestamp - Client timestamp.
 * @param {string} payload.reason - Publish stop reason.
 * @param {number} payload.publishedMs - Published duration.
 * @returns {void}
 */
const _livestreamViewerCameraPublishStopped = ({
  streamId,
  clientTimestamp,
  reason,
  publishedMs,
}) => {
  track({
    type: EventTypes.LIVESTREAM_VIEWER_CAMERA_PUBLISH_STOPPED,
    payload: {
      'stream.id': streamId,
      'time.client': clientTimestamp,
      'viewerCamera.stopReason': reason,
      'viewerCamera.publishedMs': publishedMs,
    },
  });
};

const _liveStreamExclusivePreparingTimer = ({
  streamId,
  action,
  goalId,
  nbf,
  nowUnix,
  delayMs,
  timerId,
  receivedAtUnix,
  delayFromNbf,
  firedAtUnix,
  offsetSeconds,
  currentGoalId,
  nextStatus,
  clearedAtUnix,
}) => {
  track({
    type: EventTypes.LIVESTREAM_EXCLUSIVE_PREPARING_TIMER,
    payload: {
      'stream.id': streamId,
      'timer.action': action,
      'timer.goalId': goalId ?? currentGoalId,
      'timer.nbf': nbf,
      'timer.nowUnix': nowUnix,
      'timer.delayMs': delayMs,
      'timer.timerId': timerId,
      'timer.receivedAtUnix': receivedAtUnix,
      'timer.delayFromNbf': delayFromNbf,
      'timer.firedAtUnix': firedAtUnix,
      'timer.offsetSeconds': offsetSeconds,
      'timer.nextStatus': nextStatus,
      'timer.clearedAtUnix': clearedAtUnix,
    },
  });
};

/**
 * Handle MEDIA_ENGAGEMENT event.
 * @kind mixpanel/eventType
 * @name _mediaEngagement
 * @param {string} playerType - player type ('livestream', 'vod')
 * @param {string} playerSource - player source
 * @param {string} livestreamState - livestream state
 * @param {number} giftTime - gift time
 * @param {string} giftState - gift state
 * @param {string} creatorId - creator id
 * @param {string} creatorUsername - creator username
 * @param {string} streamType - livestream stream type ('normal' or 'audio')
 * @param {number} startTime - start time
 * @param {number} endTime - end time
 * @param {boolean} isFree - is free
 * @param {boolean} isNewlyUnlocked - is newly unlocked
 * @param {string} viewScope - view scope
 * @param {string} mediaType - media type
 * @param {string} messageId - message id
 * @param {number} ntpOffsetSeconds - ntp offset seconds
 * @param {boolean} hasViewerCameraVideo - whether the viewer published video
 * @param {boolean} hasViewerCameraAudio - whether the viewer published audio
 * @param {number} exclusivePrivatePrice - 1v1 price in diamonds per minute
 */
const _mediaEngagement = ({
  playerType,
  playerSource,
  livestreamState,
  giftTime,
  giftState,
  creatorId,
  creatorUsername,
  streamType,
  startTime,
  endTime,
  isFree,
  isNewlyUnlocked,
  viewScope,
  mediaType,
  messageId,
  ntpOffsetSeconds = 0,
  hasViewerCameraVideo,
  hasViewerCameraAudio,
  exclusivePrivatePrice,
  voluumClickId,
}) => {
  if (!startTime || !endTime || startTime > endTime) return;

  let payload = {
    'player.type': playerType,
    'player.source': playerSource,
    'time.start': startTime,
    'time.end': endTime,
    'time.offsetSeconds': ntpOffsetSeconds,
    duration: (endTime - startTime) / 1000,
    time: endTime,
    'user.id': creatorId,
    'user.username': creatorUsername,
    'affiliate.voluum.cid': voluumClickId,
  };

  if (playerType === PlayerType.LIVESTREAM) {
    payload = {
      ...payload,
      'livestream.state': livestreamState,
      'livestream.streamType': streamType,
      'gift.time': giftTime,
      'gift.state': giftState,
      // viewers can only publish during a 1v1
      ...(livestreamState === EngagementLivestreamState.EXCLUSIVE_PRIVATE && {
        'user.hasVideo': hasViewerCameraVideo,
        'user.hasAudio': hasViewerCameraAudio,
        'stream.exclusive.price': exclusivePrivatePrice,
      }),
    };
  } else if (playerType === PlayerType.VOD) {
    payload = {
      ...payload,
      'vod.isFree': isFree,
      'vod.isNewlyUnlocked': isNewlyUnlocked,
      'vod.viewScope': viewScope,
      'vod.mediaType': mediaType,
      'vod.messageId': messageId,
    };
  }

  track({
    type: EventTypes.MEDIA_ENGAGEMENT,
    payload,
  });
};

/**
 * Handle the EventTypes.LIVESTREAM_GIFT_ANIMATION_PLAYED event.
 * @kind mixpanel/eventType
 * @name _livestreamGiftAnimationPlayed
 * @param {Object} payload
 * @param {string} payload.giftId - gift id.
 * @param {string} payload.streamId - stream id.
 * @param {string} payload.animationType - animation type (pag or apng).
 * @param {string} payload.modalId
 * @param {string} payload.viewId
 * @return {void}
 */
const _livestreamGiftAnimationPlayed = payload => {
  track({
    type: EventTypes.LIVESTREAM_GIFT_ANIMATION_PLAYED,
    payload: {
      'animation.type': payload?.animationType,
      'gift.id': payload?.giftId,
      'modal.id': payload?.modalId,
      'stream.id': payload?.streamId,
      'view.id': payload?.viewId,
    },
  });
};

/**
 * Handle the EventTypes.SHOP_GOOGLE_PAY_CLICKED event.
 * @kind mixpanel/eventType
 * @name _shopGooglePayClicked
 * @return {void}
 */
const _shopGooglePayClicked = () => {
  // Click count only — no payload by design.
  track({ type: EventTypes.SHOP_GOOGLE_PAY_CLICKED, payload: {} });
};
